<template>
  <div
    class="video-list-container"
    :class="{ 'navigation-collapsed': sidebarCollapsed, 'generate-panel-resizing': isGeneratePanelResizing }"
    :style="generatePanelWidthStyle"
  >
    <VideoScriptLibrary
      v-if="!route.params.scriptId"
      :scripts="preloadedScripts"
      :loading="!scriptsLoaded"
      :error="scriptsError"
      @select="openStudioScript"
      @refresh="reloadScripts"
    />
    <div v-else-if="!scriptsLoaded || scriptsError" class="video-studio-load-state" :aria-busy="!scriptsLoaded">
      <el-icon v-if="!scriptsLoaded" class="is-loading"><Loading /></el-icon>
      <p :role="scriptsError ? 'alert' : 'status'">{{ scriptsError || '正在加载剧本…' }}</p>
      <button v-if="scriptsError" type="button" class="studio-retry" @click="reloadScripts">重新加载</button>
    </div>
    <div v-else class="video-studio-shell">
    <div class="main-layout">
      <div id="video-sidebar" :inert="sidebarCollapsed ? '' : null" :aria-hidden="sidebarCollapsed">
        <Sidebar
          ref="sidebarRef"
          :user-info="userInfo"
          @workspace-selected="handleWorkspaceSelected"
          @avatar-change="handleAvatarChange"
          @password-changed="handlePasswordChanged"
          @user-info-refreshed="handleUserInfoRefreshed"
        >
          <HeaderBar
            id="video-header"
            :preloaded-scripts="preloadedScripts"
            @cache-cleaned="handleCacheCleaned"
            @open-stats="handleOpenDailyStats"
          />
        </Sidebar>
      </div>
      <div class="content-area">
        <!-- 视频列表区域 + 右侧角色展示 -->
        <div class="content-main-with-sidebar">
          <!-- 
            修改点：
            1. 添加 ref="videoContentRef" 用于脚本操作
            2. 添加 @scroll="handleScroll" 监听容器内部滚动
            3. 样式类 video-content 在 CSS 中已修改为固定高度+内部滚动
          -->
          <div
            class="video-content"
            ref="videoContentRef"
            @scroll="handleScroll"
          >
            <div class="gallery-toolbar">
              <div class="gallery-heading"><h1>视频</h1><span>我的创作</span></div>
              <div class="gallery-toolbar-actions">
                <button type="button" class="studio-toolbar-button navigation-toggle" :title="sidebarCollapsed ? '展开顶部导航' : '收起顶部导航'" :aria-label="sidebarCollapsed ? '展开顶部导航' : '收起顶部导航'" :aria-expanded="!sidebarCollapsed" aria-controls="video-sidebar" @click="toggleSidebar"><el-icon><ArrowLeft /></el-icon></button>
              </div>
            </div>
            <!-- 加载状态 -->
            <div v-loading="loading" class="video-grid" :style="{ gridTemplateColumns: `repeat(${displayVideosPerRow}, minmax(0, 1fr))` }">
              <div
                v-for="video in videoList"
                :key="video.id"
                :class="['video-card', { 'video-card-disabled': video.status !== 'completed' && video.status !== 'failed', 'video-card-failed': video.status === 'failed' }]"
                @click="handleVideoClick(video)"
                @mouseenter="handleMouseEnter(video)"
                @mouseleave="handleMouseLeave(video)"
              >
                <div class="video-thumbnail" :class="getVideoOrientationClass(video)">
                  <template v-if="video.videoUrl && video.status === 'completed'">
                  <ReferenceThumbnail v-show="playingVideoId !== video.id" :src="displayVideoUrl(video)" media-type="video" first-frame-fallback class="thumbnail-video" />
                  <video
                    v-if="hoveredVideoId === video.id"
                    :ref="el => setVideoRef(video.id, el)"
                    :src="(!isCleanedVideo(video) && cachedVideoUrls[video.id]) || displayVideoUrl(video)"
                    :key="displayVideoUrl(video)"
                    :poster="getThumbnail(displayVideoUrl(video))"
                    class="thumbnail-video"
                    :style="{ visibility: playingVideoId === video.id ? 'visible' : 'hidden' }"
                    preload="none"
                    @canplay="event => hoveredVideoId === video.id && safePlayVideo(event.target)"
                    @playing="hoveredVideoId === video.id && (playingVideoId = video.id)"
                    @pause="playingVideoId === video.id && (playingVideoId = null)"
                    @loadstart="playingVideoId === video.id && (playingVideoId = null)"
                    referrerpolicy="no-referrer"
                    muted
                    playsinline
                    loop
                    @loadedmetadata="(event) => handleVideoLoaded(video, event)"
                    @error="(event) => handleVideoError(video, event)"
                  ></video>
                  </template>
                  <div
                    v-else
                    class="video-placeholder"
                    :class="{ 'video-placeholder-processing': video.status === 'in_progress' }"
                  >
                    <div class="placeholder-content">
                      <!-- 失败状态：展示友好提示，引导用户点击查看详情 -->
                      <template v-if="video.status === 'failed'">
                        <div class="failed-hint">
                          <el-icon class="failed-hint-icon"><WarningFilled /></el-icon>
                          <span class="failed-hint-text">好像出了点问题</span>
                          <span class="failed-hint-link">点这里看看</span>
                        </div>
                      </template>
                      <!-- 其他状态：保持原有占位展示 -->
                      <template v-else>
                        <div class="placeholder-icon">
                          <template v-if="video.status === 'queued'">
                            <div class="lds-ellipsis">
                              <div></div>
                              <div></div>
                              <div></div>
                              <div></div>
                            </div>
                          </template>
                          <template v-else-if="video.status === 'in_progress'">
                            <picture class="video-running-loader" aria-hidden="true">
                              <source
                                media="(prefers-reduced-motion: reduce)"
                                srcset="@/assets/images/video-running-loader-still.webp"
                              />
                              <img
                                src="@/assets/images/video-running-loader.webp"
                                width="60"
                                height="72"
                                alt=""
                                decoding="async"
                                draggable="false"
                              />
                            </picture>
                          </template>
                          <template v-else>
                            <el-icon><VideoCamera /></el-icon>
                          </template>
                        </div>
                        <div
                          class="placeholder-status"
                          :class="{
                            'placeholder-status-queued': video.status === 'queued',
                            'placeholder-status-processing': video.status === 'in_progress'
                          }"
                        >
                          <template v-if="video.status === 'queued'">
                            <span class="queued-text">{{ video.submissionText || workflowLabel(video.workflowStatus, video.status, video.platform) || '排队中' }}</span>
                          </template>
                          <template v-else-if="video.status === 'in_progress'">
                            <span class="processing-text">{{ video.submissionText || workflowLabel(video.workflowStatus, video.status, video.platform) || '处理中' }}</span>
                          </template>
                          <template v-else>
                            {{ video.statusText }}
                          </template>
                        </div>
                      </template>
                    </div>
                  </div>
                  <div v-if="video.submissionState" class="submission-actions" @click.stop>
                    <span v-if="video.errorMessage">{{ video.errorMessage }}</span>
                    <el-button v-if="video.submissionState === 'FAILED'" size="small" @click="handleRetrySubmission(video)">重试上传</el-button>
                  </div>
                  <div class="video-duration" v-if="video.duration > 0">
                    {{ formatDuration(video.duration) }}
                  </div>
                  <!-- 平台名称标签 -->
                  <div
                    v-if="video.platform && getPlatformName(video.platform)"
                    class="platform-tag"
                    :class="getPlatformTagClass(video.platform)"
                  >
                    {{ getPlatformName(video.platform) }}
                  </div>
                  <!-- 进度条 -->
                  <div class="progress-overlay" v-if="video.status === 'in_progress' && hasVideoProgress(video)">
                    <el-progress
                      :percentage="video.progress"
                      :stroke-width="4"
                      :show-text="false"
                    />
                    <div class="progress-text">{{ video.progress }}%</div>
                  </div>
                  <!-- 编辑备注按钮（所有状态都可编辑备注） -->
                  <div
                    v-if="video"
                    v-permission="'video:update_remark'"
                    class="edit-remark-icon-wrapper"
                  >
                    <el-button
                      circle
                      class="edit-remark-icon-button"
                      :icon="Edit"
                      :disabled="!canEditRemark(video)"
                      @click.stop="handleEditRemark(video)"
                    />
                  </div>
                  <!-- 复制按钮（所有状态都可复制） -->
                  <div
                    v-if="video"
                    class="copy-icon-wrapper"
                  >
                    <el-button
                      circle
                      class="copy-icon-button"
                      :icon="CopyDocument"
                      @click.stop="handleCopyToGenerate(video)"
                    />
                  </div>
                  <VideoSubtitleControl
                    v-if="video.status === 'completed' && video.videoUrl"
                    :video="video" compact class="video-subtitle-control"
                    @change="handleSubtitleChange"
                  />
                  <!-- 悬浮下载按钮（仅已完成视频显示） -->
                  <div
                    v-if="video.videoUrl && video.status === 'completed'"
                    class="download-icon-wrapper"
                  >
                    <el-button
                      circle
                      class="download-icon-button"
                      :icon="downloadingMap[video.id] ? undefined : Download"
                      :loading="!!downloadingMap[video.id]"
                      @click.stop="handleDownload(video)"
                    />
                  </div>
                  <!-- 悬浮重试按钮（失败视频显示） -->
                  <div
                    v-if="video.status === 'failed' && hasPlatformCode(video.platform)"
                    v-permission="'video:generate'"
                    class="download-icon-wrapper"
                  >
                    <el-button
                      circle
                      class="download-icon-button"
                      :icon="retryingMap[video.id] ? undefined : RefreshRight"
                      :loading="!!retryingMap[video.id]"
                      @click.stop="handleRetry(video)"
                    />
                  </div>
                  <!-- 删除按钮 -->
                  <el-button
                    v-permission="'video:delete'"
                    :icon="Close"
                    circle
                    class="delete-button"
                    @click.stop="handleDelete(video)"
                  />
                  <!-- 刷新按钮（中心位置） -->
                  <div v-if="video.status === 'completed'" class="refresh-icon-wrapper">
                    <el-button
                      circle
                      class="refresh-icon-button"
                      :icon="refreshingMap[video.id] ? undefined : Refresh"
                      :loading="!!refreshingMap[video.id]"
                      @click.stop="handleRefreshSingleVideo(video)"
                    />
                  </div>
                  <!-- 备注信息（所有状态均显示底部悬浮层；仅已完成可点击编辑） -->
                  <div
                    v-if="video.remark"
                    class="video-remark-overlay"
                    :class="{
                      'has-progress': video.status === 'in_progress',
                      'has-animation': video.status === 'queued',
                      'is-clickable': canEditRemark(video)
                    }"
                    @click.stop="handleEditRemark(video)"
                  >
                    {{ video.remark }}
                  </div>
                </div>
              </div>
            </div>

            <!-- 空状态 -->
            <el-empty v-if="!loading && videoList.length === 0" class="gallery-empty" description="暂无视频" :image-size="56">
              <template #image><el-icon class="gallery-empty-camera"><VideoCamera /></el-icon></template>
            </el-empty>

            <!-- 加载更多提示 -->
            <div class="load-more-container" v-if="hasMore && videoList.length > 0">
              <div v-if="loadingMore" class="loading-more">
                <el-icon class="is-loading"><Loading /></el-icon>
                <span>加载中...</span>
              </div>
              <div v-else class="load-more-tip">滚动到底部加载更多</div>
            </div>

            <!-- 
              修改点：添加回到顶部按钮 
              target: 指定滚动的容器类名 (.video-content)
              bottom: 距离底部高度，设为 220 避免被底部的生成面板遮挡
            -->
            <el-backtop target=".video-content" :bottom="backtopBottom" :right="backtopRight" :visibility-height="400">
               <div class="back-top-custom">
                  <el-icon><Top /></el-icon>
               </div>
            </el-backtop>
          </div>

        </div>

        <!-- 视频生成区域 -->
        <GeneratePanel
          v-if="scriptsLoaded"
          v-permission="'video:generate'"
          ref="generatePanelRef"
          v-model:prompt="generatePrompt"
          v-model:remark="generateRemark"
          v-model:seconds="generateSeconds"
          v-model:size="generateSize"
          v-model:model="generateModel"
          v-model:platform="selectedPlatform"
          v-model:batch-count="generateBatchCount"
          v-model:input-reference="inputReference"
          :platform-options="platformOptions"
          :platform-available="platformAvailable"
          :generating="generating"
          :current-workspace-id="currentWorkspaceId"
          :script-id="currentScriptId"
          :initial-panel-width="generatePanelWidth"
          @generate="handleGenerate"
          @panel-resize="handleGeneratePanelResize"
        />
      </div>
    </div>

    <!-- 视频详情覆盖层：始终在列表之上展示，不跳转离开列表页 -->
    <transition name="video-detail-fade">
      <div
        v-if="showDetailOverlay"
        class="video-detail-overlay"
      >
        <VideoDetail
          :video-id="currentDetailVideoId"
          :use-router-back="false"
          @close="handleCloseDetailOverlay"
          @subtitle-change="handleSubtitleChange"
        />
      </div>
    </transition>

    <!-- 本地失败任务详情（审核未过、未落库，直接展示内存数据） -->
    <el-dialog
      v-model="showLocalErrorDialog"
      title="任务详情"
      width="680px"
      :close-on-click-modal="true"
      class="local-error-dialog"
    >
      <div v-if="localErrorVideo" class="local-error-detail">
        <!-- 失败状态标识 -->
        <div class="local-error-status-bar">
          <el-icon class="local-error-status-icon"><WarningFilled /></el-icon>
          <span class="local-error-status-text">生成失败</span>
          <span v-if="localErrorVideo.createTime" class="local-error-time">
            {{ new Date(localErrorVideo.createTime).toLocaleString('zh-CN', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' }) }}
          </span>
        </div>

        <!-- 友好错误提示（仅命中关键词时展示） -->
        <div v-if="getFriendlyErrorMessage(localErrorVideo.errorMessage)" class="local-error-friendly">
          <el-icon class="local-error-icon"><WarningFilled /></el-icon>
          <span>{{ getFriendlyErrorMessage(localErrorVideo.errorMessage) }}</span>
        </div>

        <!-- 原始错误信息 -->
        <div v-if="localErrorVideo.errorMessage" class="local-error-raw">
          {{ localErrorVideo.errorMessage }}
        </div>

        <!-- 提示词 -->
        <div v-if="localErrorVideo.prompt" class="local-error-section">
          <div class="local-error-label">提示词</div>
          <div class="local-error-prompt">{{ localErrorVideo.prompt }}</div>
        </div>

        <!-- 基本信息 -->
        <div class="local-error-meta">
          <div v-if="getPlatformName(localErrorVideo.platform)" class="local-error-meta-item">
            <span class="local-error-meta-key">平台</span>
            <span class="local-error-meta-val">{{ getPlatformName(localErrorVideo.platform) }}</span>
          </div>
          <div v-if="localErrorVideo.size" class="local-error-meta-item">
            <span class="local-error-meta-key">尺寸</span>
            <span class="local-error-meta-val">{{ localErrorVideo.size }}</span>
          </div>
          <div v-if="localErrorVideo.seconds" class="local-error-meta-item">
            <span class="local-error-meta-key">时长</span>
            <span class="local-error-meta-val">{{ localErrorVideo.seconds }}s</span>
          </div>
          <div v-if="localErrorVideo.remark" class="local-error-meta-item">
            <span class="local-error-meta-key">备注</span>
            <span class="local-error-meta-val">{{ localErrorVideo.remark }}</span>
          </div>
        </div>
      </div>
    </el-dialog>

    <el-dialog
      v-model="dailyStatsDialogVisible"
      width="500px"
      :close-on-click-modal="true"
      :show-close="false"
      :append-to-body="false"
      class="daily-stats-dialog"
    >
      <template #header>
        <div class="daily-stats-header">
          <div class="daily-stats-title-wrap">
            <div class="daily-stats-title-icon">
              <el-icon><DataAnalysis /></el-icon>
            </div>
            <div>
              <div class="daily-stats-title">今日生成统计</div>
              <div class="daily-stats-subtitle">当前账号今日视频生成概览</div>
            </div>
          </div>
          <button
            type="button"
            class="daily-stats-close"
            aria-label="关闭统计弹窗"
            @click="dailyStatsDialogVisible = false"
          >
            <el-icon><Close /></el-icon>
          </button>
        </div>
      </template>
      <div v-loading="dailyStatsLoading" class="daily-stats-content">
        <div class="daily-stats-date">
          <el-icon><Calendar /></el-icon>
          <span>{{ dailyStats.date || todayDateText }}</span>
        </div>
        <div class="daily-stats-grid">
          <div class="daily-stats-item daily-stats-item--count">
            <div class="daily-stats-item-head">
              <span class="daily-stats-card-icon">
                <el-icon><VideoCamera /></el-icon>
              </span>
              <span class="daily-stats-label">生成视频个数</span>
            </div>
            <div class="daily-stats-metric">
              <strong class="daily-stats-value">{{ todayGeneratedCount }}</strong>
              <span class="daily-stats-unit">个</span>
            </div>
          </div>
          <div class="daily-stats-item daily-stats-item--duration">
            <div class="daily-stats-item-head">
              <span class="daily-stats-card-icon">
                <el-icon><Timer /></el-icon>
              </span>
              <span class="daily-stats-label">生成视频时长</span>
            </div>
            <div class="daily-stats-metric">
              <strong class="daily-stats-value daily-stats-value--duration">{{ formatDurationLabel(todayGeneratedDurationSeconds) }}</strong>
            </div>
          </div>
        </div>
      </div>
    </el-dialog>

    <!-- 编辑备注对话框 -->
    <el-dialog
      v-model="editRemarkDialogVisible"
      title="编辑备注"
      width="500px"
      :close-on-click-modal="false"
      @close="handleCancelEditRemark"
    >
      <el-form
        ref="editRemarkFormRef"
        :model="editRemarkForm"
        :rules="editRemarkRules"
        label-width="80px"
        @submit.prevent
      >
        <el-form-item label="备注" prop="remark">
          <el-input
            v-model="editRemarkForm.remark"
            type="textarea"
            :rows="4"
            placeholder="请输入备注信息"
            maxlength="200"
            show-word-limit
            clearable
          />
        </el-form-item>
      </el-form>
      <template #footer>
        <span class="dialog-footer">
          <el-button @click="handleCancelEditRemark">取消</el-button>
          <el-button type="primary" :loading="updatingRemark" @click="handleSaveEditRemark">
            保存
          </el-button>
        </span>
      </template>
    </el-dialog>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted, onUnmounted, onActivated, onDeactivated, nextTick, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { ElMessage, ElMessageBox, ElBacktop } from 'element-plus' // 引入 ElBacktop
import {
  Loading,
  VideoCamera,
  Close,
  Download,
  Edit,
  RefreshRight,
  Refresh,
  CopyDocument,
  Top,
  WarningFilled,
  DataAnalysis,
  Timer,
  Calendar,
  ArrowLeft
} from '@element-plus/icons-vue'
import { getVideoRecords, getVideoGenerationStatus, deleteVideo, getVideoProgress, getSeedanceProgress, updateVideoRemark, getVideoDetail, getMyDailyStatistics } from '@/api/video'
import { getUserInfo, setUserInfo, getGenerateCount, hasPermission } from '@/api/auth'
import { SELECTED_SCRIPT_KEY, VIDEO_GEN_CONFIG_KEY } from '@/config/constants'
import { getScripts } from '@/api/scripts'
import { getOrCacheVideoUrl, getCachedVideoUrl, deleteCachedVideo } from '@/utils/videoCache'
import { withSilentMode } from '@/utils/request'
import { getPlatformConfigs } from '@/api/platform'
import Sidebar from '@/views/video/components/Sidebar.vue'
import HeaderBar from '@/views/video/components/HeaderBar.vue'
import ReferenceThumbnail from './components/ReferenceThumbnail.vue'
import VideoSubtitleControl from './components/VideoSubtitleControl.vue'
import { displayVideoUrl, isCleanedVideo } from './model/subtitleVersion'
import { referenceThumbnailUrl } from './model/referenceThumbnails'
import GeneratePanel from '@/views/video/components/GeneratePanel.vue'
import { enqueueVideoSubmissions, videoSubmissions, submissionLabels, retryVideoSubmission, submissionReferenceSnapshot } from '@/utils/videoSubmissionQueue'
import { normalizeVideoUiStatus, hasVideoProgress, wan3CdWorkflowLabel } from './model/videoProgress'
import wan3ReferencePolicy from './model/wan3ReferencePolicy'
import h3VideoPolicy from './model/h3VideoPolicy'
import VideoDetail from '@/views/video/VideoDetail.vue'
import { startVideoUploadTask, stopVideoUploadTask, setUpdateCallback } from '@/tasks/videoUploadTask'
import { referenceDisplayUrl, parseReferenceFiles } from './model/referenceUrls'
import VideoScriptLibrary from './VideoScriptLibrary.vue'

export default {
  name: 'VideoListPage',
  components: {
    ReferenceThumbnail,
    VideoSubtitleControl,
    Loading,
    VideoCamera,
    Sidebar,
    HeaderBar,
    GeneratePanel,
    VideoDetail,
    ElBacktop, // 注册组件
    Top, // 注册图标
    WarningFilled,
    DataAnalysis,
    Timer,
    Calendar,
    ArrowLeft,
    VideoScriptLibrary
  },
  setup() {
    const route = useRoute()
    const router = useRouter()
    const videoList = ref([])
    const scriptsLoaded = ref(false)
    const scriptsError = ref('')
    const preloadedScripts = ref([]) // 预加载的脚本数据
    const currentPage = ref(1)
    const pageSize = ref(20)
    const total = ref(0)
    const loading = ref(false)
    const loadingMore = ref(false)
    const hasMore = ref(true)
    const generating = ref(false)
    const generatePrompt = ref('')
    const generateRemark = ref('')
    const generateSeconds = ref('5')
    const generateSize = ref('496x864')
    const generateModel = ref('')
    const generateBatchCount = ref(1)
    const platformOptions = ref([]) // 平台列表
    const selectedPlatform = ref('') // 当前选择的平台编码
    const platformAvailable = computed(() => platformOptions.value.some(item => item.platformCode === selectedPlatform.value))
    let platformConfigLoading = false
    let platformRefreshTimer = null
    let platformConfigsDisposed = false
    const applyingGenerateConfig = ref(false)
    
    // 滚动相关
    const scrollPosition = ref(0) // 列表滚动位置缓存
    const videoContentRef = ref(null) // 视频容器引用，用于控制滚动

    const inputReference = ref('') // 上传的图片URL
    const generatePanelRef = ref(null) // GeneratePanel 组件引用
    const DEFAULT_GENERATE_PANEL_WIDTH = 392
    const GENERATE_PANEL_WIDTH_KEY = 'video_generate_panel_width'
    const getStoredGeneratePanelWidth = () => {
      try {
        const width = Number.parseInt(localStorage.getItem(GENERATE_PANEL_WIDTH_KEY), 10)
        return Number.isFinite(width) && width > 0 ? width : DEFAULT_GENERATE_PANEL_WIDTH
      } catch (e) {
        return DEFAULT_GENERATE_PANEL_WIDTH
      }
    }
    const persistGeneratePanelWidth = (width) => {
      try {
        localStorage.setItem(GENERATE_PANEL_WIDTH_KEY, String(Math.round(width)))
      } catch (e) {
        // ignore localStorage 写入错误
      }
    }
    const generatePanelWidth = ref(getStoredGeneratePanelWidth())
    const generatePanelWidthStyle = computed(() => ({
      '--generate-panel-width': `${generatePanelWidth.value}px`
    }))
    const isGeneratePanelResizing = ref(false)
    let videoContentResizeObserver = null
    let updateDisplayVideosPerRowRaf = null
    const hoveredVideoId = ref(null)
    const playingVideoId = ref(null)
    const videoRefs = ref({}) // 存储视频元素的引用
    const userInfo = ref(getUserInfo()) // 用户信息
    const sidebarCollapsed = ref(false) // 工作空间和用户信息默认显示在顶部
    const handleSubtitleChange = ({ id, subtitleRemoval }) => {
      const video = videoList.value.find(item => String(item.id) === String(id))
      if (video) video.subtitleRemoval = subtitleRemoval
    }

    const cachedVideoUrls = ref({}) // 缓存的视频 URL 映射 { videoId: cachedUrl }
    const downloadingMap = ref({}) // 每个视频的下载加载状态 { videoId: boolean }
    const retryingMap = ref({}) // 每个视频的重试加载状态 { videoId: boolean }
    const refreshingMap = ref({}) // 每个视频的刷新加载状态 { videoId: boolean }
    const dailyStatsDialogVisible = ref(false)
    const dailyStatsLoading = ref(false)
    const dailyStats = ref({
      date: '',
      totalCount: null,
      failedCount: null,
      nonFailedCount: null,
      durationSeconds: null,
      nonFailedDurationSeconds: null
    })

    // 编辑备注相关
    const editRemarkDialogVisible = ref(false) // 编辑备注对话框显示状态
    const editRemarkFormRef = ref(null) // 编辑备注表单引用
    const updatingRemark = ref(false) // 更新备注中状态
    const editRemarkForm = ref({
      videoId: null,
      remark: ''
    })
    const editRemarkRules = {
      remark: [
        { max: 200, message: '备注不能超过 200 个字符', trigger: 'blur' }
      ]
    }

    // 当前工作空间信息（由 Sidebar 通过事件通知）
    const currentWorkspaceId = ref(null) // 当前选中的工作空间ID
    const currentWorkspaceSystemPrompt = ref('') // 当前工作空间的系统提示词 / 全局指令

    const currentScriptId = ref(null)

    const syncCurrentScriptId = () => {
      const routeScriptId = route.params.scriptId
      if (routeScriptId !== undefined && routeScriptId !== null && routeScriptId !== '') {
        currentScriptId.value = routeScriptId
        try {
          const script = preloadedScripts.value.find(item => String(item.id) === String(routeScriptId))
          if (script) localStorage.setItem(SELECTED_SCRIPT_KEY, JSON.stringify(script))
        } catch (e) {
          // ignore localStorage errors
        }
        return
      }
      currentScriptId.value = null
      try {
        localStorage.removeItem(SELECTED_SCRIPT_KEY)
      } catch (e) {
        // ignore localStorage errors
      }
    }

    // 每行显示视频数量
    const VIDEOS_PER_ROW_KEY = 'videos_per_row'
    const videosPerRow = ref(localStorage.getItem(VIDEOS_PER_ROW_KEY) || '5')
    const displayVideosPerRow = ref(Number(videosPerRow.value) || 5)
    const isNarrowViewport = ref(false)
    const updateViewportState = () => {
      isNarrowViewport.value = window.innerWidth <= 900
      scheduleUpdateDisplayVideosPerRow()
    }
    const backtopBottom = computed(() => (isNarrowViewport.value ? 220 : 40))
    const backtopRight = computed(() => (isNarrowViewport.value ? 40 : generatePanelWidth.value + 28))
    const videosPerRowOptions = [
      { label: '3个/行', value: '3' },
      { label: '4个/行', value: '4' },
      { label: '5个/行', value: '5' },
      { label: '6个/行', value: '6' },
      { label: '7个/行', value: '7' },
      { label: '8个/行', value: '8' }
    ]

    // 处理每行显示数量变化
    const handleVideosPerRowChange = (value) => {
      videosPerRow.value = value
      localStorage.setItem(VIDEOS_PER_ROW_KEY, value)
      scheduleUpdateDisplayVideosPerRow()
    }

    const getVideoGridAvailableWidth = () => {
      const el = videoContentRef.value
      if (!el) return 0
      const styles = window.getComputedStyle(el)
      const paddingLeft = parseFloat(styles.paddingLeft) || 0
      const paddingRight = parseFloat(styles.paddingRight) || 0
      return Math.max(0, el.clientWidth - paddingLeft - paddingRight)
    }

    const updateDisplayVideosPerRow = () => {
      const maxColumns = Number(videosPerRow.value) || 5
      if (isNarrowViewport.value) {
        displayVideosPerRow.value = Math.min(maxColumns, window.innerWidth <= 480 ? 2 : 3)
        return
      }

      const availableWidth = getVideoGridAvailableWidth()
      if (!availableWidth) {
        displayVideosPerRow.value = maxColumns
        return
      }

      const gridGap = 20
      const minCardWidth = 220
      const fittingColumns = Math.max(1, Math.floor((availableWidth + gridGap) / (minCardWidth + gridGap)))
      displayVideosPerRow.value = Math.max(1, Math.min(maxColumns, fittingColumns))
    }

    const scheduleUpdateDisplayVideosPerRow = () => {
      if (updateDisplayVideosPerRowRaf) return
      updateDisplayVideosPerRowRaf = requestAnimationFrame(() => {
        updateDisplayVideosPerRowRaf = null
        updateDisplayVideosPerRow()
      })
    }

    const handleGeneratePanelResize = (payload) => {
      const width = typeof payload === 'number' ? payload : payload?.width
      if (Number.isFinite(width)) {
        generatePanelWidth.value = width
        if (payload?.persist) persistGeneratePanelWidth(width)
      }
      isGeneratePanelResizing.value = Boolean(payload?.resizing)
      scheduleUpdateDisplayVideosPerRow()
    }

    // 详情覆盖层状态
    const showDetailOverlay = ref(false)
    const currentDetailVideoId = ref(null)

    const sidebarRef = ref(null)

    const toggleSidebar = () => {
      sidebarCollapsed.value = !sidebarCollapsed.value
      nextTick(() => {
        scheduleUpdateDisplayVideosPerRow()
        window.dispatchEvent(new Event('resize'))
      })
    }

    const getTodayNoonMillis = () => {
      const today = new Date()
      today.setHours(12, 0, 0, 0)
      return today.getTime()
    }

    const todayDateText = computed(() => {
      const today = new Date()
      const year = today.getFullYear()
      const month = String(today.getMonth() + 1).padStart(2, '0')
      const day = String(today.getDate()).padStart(2, '0')
      return `${year}-${month}-${day}`
    })

    const isTodayVideo = (video) => {
      const rawTime = video?.createTime || video?.createdAt
      if (!rawTime) return false
      const time = typeof rawTime === 'number' ? rawTime : Number(rawTime)
      if (!Number.isFinite(time)) return false
      const videoDate = new Date(time)
      return videoDate.getFullYear() === new Date().getFullYear() &&
        videoDate.getMonth() === new Date().getMonth() &&
        videoDate.getDate() === new Date().getDate()
    }

    const getVideoSeconds = (video) => {
      const seconds = Number(video?.seconds ?? video?.duration ?? 0)
      return Number.isFinite(seconds) && seconds > 0 ? seconds : 0
    }

    const getOptionalNonNegativeNumber = (value) => {
      if (value === null || value === undefined || value === '') return null
      const number = Number(value)
      return Number.isFinite(number) && number >= 0 ? number : null
    }

    const loadedTodayVideos = computed(() => videoList.value.filter(isTodayVideo))

    const loadedTodayDurationSeconds = computed(() => loadedTodayVideos.value.reduce((sum, video) => {
      if (video.status === 'failed') return sum
      return sum + getVideoSeconds(video)
    }, 0))

    const todayGeneratedDurationSeconds = computed(() => {
      const nonFailedDuration = getOptionalNonNegativeNumber(dailyStats.value.nonFailedDurationSeconds)
      if (nonFailedDuration !== null) {
        return nonFailedDuration
      }
      const duration = getOptionalNonNegativeNumber(dailyStats.value.durationSeconds)
      if (duration !== null) {
        return duration
      }
      return loadedTodayDurationSeconds.value
    })

    const todayGeneratedCount = computed(() => {
      const nonFailedCount = getOptionalNonNegativeNumber(dailyStats.value.nonFailedCount)
      if (nonFailedCount !== null) {
        return nonFailedCount
      }
      return loadedTodayVideos.value.filter(video => video.status !== 'failed').length
    })

    const formatDurationLabel = (seconds) => {
      const secs = Math.max(0, Math.floor(Number(seconds) || 0))
      if (secs < 60) return `${secs} 秒`
      const hours = Math.floor(secs / 3600)
      const minutes = Math.floor((secs % 3600) / 60)
      const rest = secs % 60
      if (hours > 0) {
        return `${hours} 小时 ${String(minutes).padStart(2, '0')} 分 ${String(rest).padStart(2, '0')} 秒`
      }
      return `${minutes} 分 ${String(rest).padStart(2, '0')} 秒`
    }

    const loadDailyStats = async () => {
      try {
        dailyStatsLoading.value = true
        const response = await withSilentMode(() => getMyDailyStatistics(getTodayNoonMillis()))
        const data = response?.data || {}
        dailyStats.value = {
          date: data.date || todayDateText.value,
          totalCount: getOptionalNonNegativeNumber(data.totalCount),
          failedCount: getOptionalNonNegativeNumber(data.failedCount),
          nonFailedCount: getOptionalNonNegativeNumber(data.nonFailedCount),
          durationSeconds: getOptionalNonNegativeNumber(data.durationSeconds),
          nonFailedDurationSeconds: getOptionalNonNegativeNumber(data.nonFailedDurationSeconds)
        }
      } catch (error) {
        // 统计失败时保留本页已加载记录的兜底展示
        dailyStats.value = {
          ...dailyStats.value,
          totalCount: null,
          failedCount: null,
          nonFailedCount: null,
          durationSeconds: null,
          nonFailedDurationSeconds: null
        }
      } finally {
        dailyStatsLoading.value = false
      }
    }

    const handleOpenDailyStats = () => {
      dailyStatsDialogVisible.value = true
      loadDailyStats()
    }

    const readVideoGenerateConfig = () => {
      try {
        const raw = localStorage.getItem(VIDEO_GEN_CONFIG_KEY)
        return raw ? JSON.parse(raw) : null
      } catch (e) {
        return null
      }
    }

    const DISPLAY_PLATFORM_CODE_MAP = {
      seedance: ['seedance'],
      'seedance-fast': ['seedance-fast'],
      wan3: ['wan3'],
      'wan3-cd': ['wan3-cd'],
      'runninghub-h3': ['runninghub-h3']
    }

    const hasPlatformCode = (platformCode) => platformOptions.value.some((item) => item.platformCode === platformCode)

    const getFirstAvailablePlatformCode = (platformCodes) => platformCodes.find(hasPlatformCode) || ''

    const getDefaultPlatformCode = () => {
      const standardPlatform = getFirstAvailablePlatformCode(['seedance'])
      if (standardPlatform) return standardPlatform

      const supportedPlatform = getFirstAvailablePlatformCode(Object.values(DISPLAY_PLATFORM_CODE_MAP).flat())
      if (supportedPlatform) return supportedPlatform

      const defaultPlatform = platformOptions.value.find((item) => item.isDefault)
      return defaultPlatform?.platformCode || platformOptions.value[0]?.platformCode || ''
    }

    const getVideoSizeOptions = (platformCode) => {
      if (h3VideoPolicy.isH3Platform(platformCode)) return h3VideoPolicy.SIZES
      if (wan3ReferencePolicy.isWanPlatform(platformCode)) {
        return [
          '864x496',
          '496x864',
          '1280x720',
          '720x1280',
          // '1920x1080',
          // '1080x1920',
          // '1440x1440',
          // '1664x1248',
          // '1248x1664'
        ]
      }
      if (platformCode === 'seedance-fast' || platformCode === 'seedance') {
        return ['864x496', '496x864', '1280x720', '720x1280']
      }
      return ['1280x720', '720x1280']
    }

    const getVideoDurationOptions = (platformCode) => {
      if (h3VideoPolicy.isH3Platform(platformCode)) return [h3VideoPolicy.DEFAULT_SECONDS]
      if (wan3ReferencePolicy.isWanPlatform(platformCode)) {
        return Array.from({ length: 29 }, (_, i) => String(i + 2))
      }
      if (platformCode === 'seedance' || platformCode === 'seedance-fast') {
        return Array.from({ length: 12 }, (_, i) => String(i + 4))
      }
      return ['10', '15']
    }

    const getVideoModelOptions = (platformCode) => {
      if (h3VideoPolicy.isH3Platform(platformCode)) return [h3VideoPolicy.MODEL]
      if (wan3ReferencePolicy.isWanPlatform(platformCode)) {
        return ['wan3.0-video']
      }
      return []
    }

    const getFallbackGenerateConfig = (platformCode) => {
      if (h3VideoPolicy.isH3Platform(platformCode)) {
        return { seconds: h3VideoPolicy.DEFAULT_SECONDS, size: h3VideoPolicy.DEFAULT_SIZE, batchCount: 1 }
      }
      if (wan3ReferencePolicy.isWanPlatform(platformCode)) {
        return { seconds: '5', size: '496x864', batchCount: 1 }
      }
      if (platformCode === 'seedance' || platformCode === 'seedance-fast') {
        return { seconds: '5', size: '496x864', batchCount: 1 }
      }
      return { seconds: '10', size: '1280x720', batchCount: 1 }
    }

    const applyVideoGenerateConfig = (savedConfig = readVideoGenerateConfig()) => {
      if (!platformOptions.value.length) return

      applyingGenerateConfig.value = true
      try {
        const savedPlatform = savedConfig?.platform
        const platform = platformOptions.value.some((item) => item.platformCode === savedPlatform)
          ? savedPlatform
          : getDefaultPlatformCode()
        const fallback = getFallbackGenerateConfig(platform)
        const durationOptions = getVideoDurationOptions(platform)
        const sizeOptions = getVideoSizeOptions(platform)
        const savedSeconds = savedConfig?.seconds != null ? String(savedConfig.seconds) : ''
        const savedSize = savedConfig?.size || ''
        const savedModel = savedConfig?.model || ''
        const savedBatchCount = Number(savedConfig?.batchCount)

        selectedPlatform.value = platform
        const validSeconds = h3VideoPolicy.isH3Platform(platform)
          ? h3VideoPolicy.isValidSeconds(savedSeconds) : durationOptions.includes(savedSeconds)
        generateSeconds.value = validSeconds ? savedSeconds : fallback.seconds
        generateSize.value = sizeOptions.includes(savedSize) ? savedSize : fallback.size
        const modelOptions = getVideoModelOptions(platform)
        generateModel.value = modelOptions.includes(savedModel)
          ? savedModel
          : (modelOptions[0] || '')
        generateBatchCount.value = Number.isFinite(savedBatchCount)
          ? Math.min(5, Math.max(1, savedBatchCount))
          : fallback.batchCount
      } finally {
        applyingGenerateConfig.value = false
      }
    }

    const saveVideoGenerateConfig = () => {
      if (applyingGenerateConfig.value || !selectedPlatform.value) return

      try {
        localStorage.setItem(VIDEO_GEN_CONFIG_KEY, JSON.stringify({
          platform: selectedPlatform.value,
          size: generateSize.value,
          seconds: generateSeconds.value,
          model: generateModel.value,
          batchCount: generateBatchCount.value
        }))
      } catch (e) {
        // ignore localStorage 写入错误
      }
    }

    watch(
      [selectedPlatform, generateSize, generateSeconds, generateModel, generateBatchCount],
      saveVideoGenerateConfig
    )

    // 加载平台配置（只加载类型为1的生视频平台）
    const loadPlatformConfigs = async () => {
      if (platformConfigLoading || platformConfigsDisposed) return
      platformConfigLoading = true
      try {
        const res = await getPlatformConfigs(1)
        if (platformConfigsDisposed) return
        if (res.code === 200 && Array.isArray(res.data)) {
          const supportedPlatformCodes = new Set(Object.values(DISPLAY_PLATFORM_CODE_MAP).flat())
          const availablePlatforms = res.data.filter(item => supportedPlatformCodes.has(item.platformCode) && item.isEnabled !== false)
          if (availablePlatforms.length > 0) {
            // 平台选择与后端平台代码一一对应，顺序保持稳定。
            const platformDisplayOrder = {
              seedance: 0,
              'seedance-fast': 1,
              wan3: 2,
              'wan3-cd': 3,
              'runninghub-h3': 4
            }
            const sortedPlatforms = [...availablePlatforms].sort((a, b) => {
              const orderA = platformDisplayOrder[a.platformCode] ?? 100
              const orderB = platformDisplayOrder[b.platformCode] ?? 100
              if (orderA !== orderB) return orderA - orderB

              const aTime = typeof a.createdAt === 'number' ? a.createdAt : Number(a.createdAt) || 0
              const bTime = typeof b.createdAt === 'number' ? b.createdAt : Number(b.createdAt) || 0
              return bTime - aTime
            })
            // 使用后端返回的 platformName 作为展示名称，默认平台后面追加「（默认）」
            platformOptions.value = sortedPlatforms.map((item) => ({
              ...item,
              label: item.platformName
                ? `${item.platformName}${item.isDefault ? '（默认）' : ''}`
                : item.platformCode
            }))
            // 仅首次选择时恢复配置；刷新不能替换已停用的选择或重置用户参数。
            if (!selectedPlatform.value) applyVideoGenerateConfig()
          } else {
            platformOptions.value = []
          }
        }
      } catch (error) {
        // 请求失败不代表模型停用，保留上次成功读取的列表并等待下次刷新。
      } finally {
        platformConfigLoading = false
      }
    }

    const startPlatformRefresh = () => {
      if (platformRefreshTimer !== null) return
      platformRefreshTimer = setInterval(loadPlatformConfigs, 60000)
    }

    const stopPlatformRefresh = () => {
      if (platformRefreshTimer === null) return
      clearInterval(platformRefreshTimer)
      platformRefreshTimer = null
    }

    const openStudioScript = (script) => {
      if (script?.id === undefined || script?.id === null) return
      router.push({ name: 'VideoStudio', params: { scriptId: String(script.id) } })
    }

    const reloadScripts = async () => {
      scriptsLoaded.value = false
      scriptsError.value = ''
      try {
        const response = await getScripts({ includeImageCover: true })
        const data = Array.isArray(response) ? response : (Array.isArray(response?.data) ? response.data : response?.data?.data)
        preloadedScripts.value = Array.isArray(data) ? data : []
        if (route.params.scriptId && !preloadedScripts.value.some(item => String(item.id) === String(route.params.scriptId))) {
          router.replace({ name: 'VideoList' })
        }
      } catch (error) {
        scriptsError.value = '剧本加载失败，请重试'
      } finally {
        scriptsLoaded.value = true
        syncCurrentScriptId()
      }
    }

    watch(() => route.params.scriptId, async scriptId => {
      if (!scriptId) {
        syncCurrentScriptId()
        currentWorkspaceId.value = null
        currentWorkspaceSystemPrompt.value = ''
        videoList.value = []
        stopStatusUpdate()
        return
      }
      if (!scriptsLoaded.value) return
      const script = preloadedScripts.value.find(item => String(item.id) === String(scriptId))
      if (!script) {
        router.replace({ name: 'VideoList' })
        return
      }
      syncCurrentScriptId()
      currentWorkspaceId.value = null
      currentWorkspaceSystemPrompt.value = ''
      videoList.value = []
      try {
        await sidebarRef.value?.loadWorkspaceList?.({ reloadVideos: true, keepSelection: false })
      } catch (e) {
        // The sidebar will retry its own workspace request when it mounts.
      }
      loadVideoList(false)
    })

    // Sidebar 通知当前工作空间变更
    const handleWorkspaceSelected = (payload) => {
      const { id, systemPrompt } = payload || {}
      currentWorkspaceId.value = id || null
      currentWorkspaceSystemPrompt.value = systemPrompt || ''
      // 当 workspaceId 为空时（例如：退出登录 / 无任何工作空间），
      // 只清空前端视频列表，不再请求后端，避免缺少 workspaceId 参数导致 500。
      if (!id) {
        videoList.value = []
        total.value = 0
        hasMore.value = false
        return
      }
      // 选中具体工作空间后，根据 workspaceId 重新加载视频列表
      loadVideoList(false)
    }

    // 判断是否需要过滤掉的模型（包含 banana 或 image）
    const isFilteredModel = (model) => {
      if (!model) return false
      const lower = String(model).toLowerCase()
      return lower.includes('banana') || lower.includes('image')
    }

    // 获取状态文本
    const getStatusText = (status) => {
      const statusMap = {
        'pending': '等待中',
        'queued': '排队中',
        'in_progress': '处理中',
        'completed': '已完成',
        'failed': '失败'
      }
      return statusMap[normalizeVideoUiStatus(status)] || status
    }

    // Extract the most useful provider/backend message from either a normal
    // response or an Axios error. Wan3 returns HTTP 200 with data.status=failed
    // when Alibaba rejects a task, so callers must inspect both layers.
    const getGenerationErrorMessage = (source, fallback = '生成视频失败，请稍后重试') => {
      const responseData = source?.response?.data || source?.data || source
      const candidates = [
        responseData?.errorMessage,
        responseData?.error_message,
        responseData?.message,
        responseData?.error?.message,
        source?.message
      ]
      const message = candidates.find(value => typeof value === 'string' && value.trim())
      return message ? message.trim() : fallback
    }

    // 获取状态类型
    const getStatusType = (status) => {
      const typeMap = {
        'pending': 'info',
        'queued': 'warning',
        'in_progress': 'warning',
        'completed': 'success',
        'failed': 'danger'
      }
      return typeMap[normalizeVideoUiStatus(status)] || 'info'
    }

    // 缩略图只用于展示，videoUrl 始终保留原始视频地址。
    const getThumbnail = url => referenceThumbnailUrl(url, 'video')

    // 设置视频引用
    const setVideoRef = (videoId, el) => {
      if (el) {
        videoRefs.value[videoId] = el
      } else {
        delete videoRefs.value[videoId]
      }
    }

    // 根据platform获取platformName
    const getPlatformName = (platformCode) => {
      if (!platformCode) return ''
      const platform = platformOptions.value.find(p => p.platformCode === platformCode)
      return platform?.platformName || ''
    }

    // Keep each platform's label colour stable as records are reordered.
    const getPlatformTagClass = (platformCode) => {
      const code = String(platformCode || '')
      let hash = 0
      for (let i = 0; i < code.length; i++) {
        hash = ((hash << 5) - hash + code.charCodeAt(i)) | 0
      }
      return `platform-tag--${['blue', 'green', 'yellow', 'purple'][Math.abs(hash) % 4]}`
    }

    // 格式化缓存大小
    const handleCacheCleaned = () => {
      Object.values(cachedVideoUrls.value).forEach((url) => {
        if (url && typeof url === 'string' && url.startsWith('blob:')) {
          try {
            URL.revokeObjectURL(url)
          } catch (e) {
            // 忽略错误
          }
        }
      })
      cachedVideoUrls.value = {}
    }

    // 加载缓存的视频 URL
    const loadCachedVideoUrl = async (videoId, videoUrl) => {
      if (!videoUrl) return
      try {
        const cachedUrl = await getCachedVideoUrl(videoUrl)
        if (cachedUrl && cachedUrl !== videoUrl) {
          cachedVideoUrls.value = {
            ...cachedVideoUrls.value,
            [videoId]: cachedUrl
          }
        }
      } catch (error) {
        // ignore
      }
    }

    // 视频加载完成处理：同时根据真实宽高回写 size，用于精确判断横竖屏
    const handleVideoLoaded = (videoItem, event) => {
      const videoEl = event?.target
      if (!videoEl) return
      // 设置视频当前时间为 0.1，显示第一帧
      try {
        videoEl.currentTime = 0.1
      } catch (e) {
        // 忽略异常
      }
      // 用真实宽高回写 size，便于 getVideoOrientationClass 判断竖屏/横屏
      const { videoWidth, videoHeight } = videoEl
      if (videoWidth && videoHeight && videoItem) {
        // 写成 "宽x高" 与后端保持一致
        videoItem.size = `${videoWidth}x${videoHeight}`
      }
    }

    // 安全地播放视频（检查元素是否仍在 DOM 中）
    const safePlayVideo = (videoElement) => {
      if (!videoElement) return
      // 检查元素是否仍在 DOM 中
      if (!document.contains(videoElement)) {
        return
      }
      // 检查元素是否仍然有效
      try {
        const playPromise = videoElement.play()
        if (playPromise !== undefined) {
          playPromise.catch(() => {
            // 忽略 "play() was interrupted" 错误
          })
        }
      } catch (error) {
        // ignore
      }
    }

    // 安全地暂停视频
    const safePauseVideo = (videoElement) => {
      if (!videoElement) return
      // 检查元素是否仍在 DOM 中
      if (!document.contains(videoElement)) {
        return
      }
      try {
        videoElement.pause()
        // 重置到第一帧
        if (videoElement.readyState >= 2) {
          videoElement.currentTime = 0.1
        }
      } catch (error) {
        // ignore
      }
    }

    // 鼠标悬停时播放视频
    const handleMouseEnter = async (video) => {
      if (!video.videoUrl || video.status !== 'completed') return
      hoveredVideoId.value = video.id
      await nextTick()
      if (hoveredVideoId.value === video.id) safePlayVideo(videoRefs.value[video.id])
      // Reuse an existing browser cache only after interaction; don't download every result.
      if (!isCleanedVideo(video)) void loadCachedVideoUrl(video.id, video.videoUrl)
    }

    const handleMouseLeave = (video) => {
      if (playingVideoId.value === video.id) playingVideoId.value = null
      safePauseVideo(videoRefs.value[video.id])
      if (hoveredVideoId.value === video.id) hoveredVideoId.value = null
    }

    const handleVideoError = (video, event) => {
      if (playingVideoId.value === video?.id) playingVideoId.value = null
      if (!video?.videoUrl || !event?.target) return
      const videoElement = event.target
      const currentSrc = videoElement.src
      const retryCount = Number(videoElement.dataset.retryCount || 0)
      
      // 如果当前使用的是缓存 URL（blob URL），回退到原始 URL
      if (currentSrc.startsWith('blob:') && retryCount === 0) {
        // 清除缓存的 URL，使用原始 URL
        if (cachedVideoUrls.value[video.id]) {
          delete cachedVideoUrls.value[video.id]
        }
        videoElement.dataset.retryCount = '1'
        videoElement.src = displayVideoUrl(video)
        videoElement.load()
        return
      }
      
      // 如果已经是原始 URL 且重试次数未超限，添加时间戳重试
      if (retryCount < 2) {
        videoElement.dataset.retryCount = retryCount + 1
        const separator = displayVideoUrl(video).includes('?') ? '&' : '?'
        videoElement.src = `${displayVideoUrl(video)}${separator}_retry=${Date.now()}`
        videoElement.load()
      }
    }

    // 下载单个视频（列表页）
    const handleDownload = async (video) => {
      if (!video || !video.videoUrl || video.status !== 'completed') {
        ElMessage.warning('视频不可下载')
        return
      }
      // 已在下载中，防止重复点击
      if (downloadingMap.value[video.id]) {
        return
      }
      try {
        downloadingMap.value = {
          ...downloadingMap.value,
          [video.id]: true
        }
        // 优先从缓存获取视频 URL
        const sourceUrl = displayVideoUrl(video)
        const preferredUrl = await getOrCacheVideoUrl(sourceUrl)
        const cachedUrl = sourceUrl === video.videoUrl && cachedVideoUrls.value[video.id]
        const videoUrl = preferredUrl || cachedUrl || sourceUrl
        // 如果拿到的是新的 blob URL，更新本地缓存映射
        if (sourceUrl === video.videoUrl && videoUrl && videoUrl !== cachedUrl && videoUrl.startsWith('blob:')) {
          cachedVideoUrls.value = {
            ...cachedVideoUrls.value,
            [video.id]: videoUrl
          }
        }
        if (!videoUrl) {
          throw new Error('视频地址无效')
        }
        // 如果是 blob URL，直接使用现有 blob 地址下载（不再二次 fetch，避免 ERR_FILE_NOT_FOUND）
        if (videoUrl.startsWith('blob:')) {
          const link = document.createElement('a')
          link.href = videoUrl
          link.download = `video_${video.id || Date.now()}.mp4`
          document.body.appendChild(link)
          link.click()
          document.body.removeChild(link)
          ElMessage.success('下载开始')
          return
        }
        // 普通 URL，优先使用 fetch 下载（可自定义文件名）
        try {
          const response = await fetch(videoUrl, { referrerPolicy: 'no-referrer' })
          if (!response.ok) {
            throw new Error('下载失败')
          }
          const blob = await response.blob()
          const url = window.URL.createObjectURL(blob)
          const link = document.createElement('a')
          link.href = url
          // 从 URL 中提取文件名，或使用默认名称
          const urlParts = videoUrl.split('/')
          const fileName = urlParts[urlParts.length - 1] || `video_${video.id || Date.now()}.mp4`
          link.download = fileName
          document.body.appendChild(link)
          link.click()
          document.body.removeChild(link)
          window.URL.revokeObjectURL(url)
          ElMessage.success('下载开始')
        } catch (fetchError) {
          // fetch 失败（如 CORS 错误），降级到直接打开 URL
          // 注意：跨域时 download 属性会被忽略，浏览器将使用服务器返回的文件名
          const link = document.createElement('a')
          link.href = videoUrl
          link.target = '_blank'
          link.rel = 'noopener noreferrer'
          document.body.appendChild(link)
          link.click()
          document.body.removeChild(link)
          ElMessage.success('正在下载...')
        }
      } catch (error) {
        ElMessage.error('下载失败，请稍后重试')
      } finally {
        downloadingMap.value = {
          ...downloadingMap.value,
          [video.id]: false
        }
      }
    }

    // 失败视频：重试生成（创建一条新任务，参数尽量沿用失败记录）
    const handleRetry = async (video) => {
      if (!video || video.status !== 'failed') {
        return
      }
      // 已在重试中，防止重复点击
      if (retryingMap.value[video.id]) {
        return
      }
      const remainingCount = userInfo.value?.generateCount
      if (typeof remainingCount === 'number' && remainingCount < 1) {
        ElMessage.warning(`剩余生成次数不足，当前剩余 ${remainingCount} 次`)
        return
      }
      const retryPrompt = (video.prompt || video.title || '').toString().trim()
      if (!retryPrompt) {
        ElMessage.warning('缺少提示词，无法重试')
        return
      }
      try {
        retryingMap.value = {
          ...retryingMap.value,
          [video.id]: true
        }
        const retryData = {
          prompt: retryPrompt,
          // seconds：优先用后端记录原值，其次用本地 duration，再兜底当前面板值
          seconds: (video.seconds != null && String(video.seconds).trim() !== '')
            ? String(video.seconds)
            : (video.duration ? String(video.duration) : generateSeconds.value),
          size: video.size || generateSize.value,
          platform: video.platform || undefined,
          workspaceId: video.workspaceId || currentWorkspaceId.value || undefined
        }
        // 备注：新任务 remark 与当前失败记录保持一致
        if (video.remark != null && String(video.remark).trim() !== '') {
          retryData.remark = String(video.remark).trim()
        }
        // 参考图：沿用失败记录
        if (video.inputReference && !h3VideoPolicy.isH3Platform(video.platform)) {
          retryData.inputReference = video.inputReference
        }
        // 水印：沿用失败记录
        if (video.watermark !== undefined) {
          retryData.watermark = video.watermark
        }
        // 支持多资源的平台：使用固定模型并从 referenceFiles 解析资源
        if (wan3ReferencePolicy.isWanPlatform(video.platform)) {
          retryData.model = 'wan3.0-video'
        }
        if (h3VideoPolicy.isH3Platform(video.platform)) {
          retryData.model = h3VideoPolicy.MODEL
        }
        if (video.platform === 'seedance') {
          retryData.model = 'ep-20260407113743-b8w5s'
        }
        // Seedance Fast 平台
        if (video.platform === 'seedance-fast') {
          retryData.model = 'ep-20260417185323-2cpl7'
        }
        if ((h3VideoPolicy.isH3Platform(video.platform) || wan3ReferencePolicy.isWanPlatform(video.platform) || video.platform === 'seedance' || video.platform === 'seedance-fast') && video.referenceFiles) {
          try {
            const refs = typeof video.referenceFiles === 'string'
              ? JSON.parse(video.referenceFiles)
              : video.referenceFiles
            if (refs.images?.length) retryData.images = refs.images.map(i => ({ url: i.url || i.sourceUrl || i.mediaUrl, role: i.role, volcAssetId: i.volcAssetId }))
            if (refs.videos?.length) retryData.videos = refs.videos.map(v => ({ url: v.url || v.sourceUrl || v.mediaUrl, duration: v.duration, volcAssetId: v.volcAssetId }))
            if (refs.audios?.length) retryData.audios = refs.audios.map(a => ({ url: a.url || a.sourceUrl || a.mediaUrl, duration: a.duration, volcAssetId: a.volcAssetId }))
          } catch (e) { /* ignore parse error */ }
        }
        await enqueueVideoSubmissions(retryData, retryData, 1)
        syncSubmissionCards()
        startStatusUpdate()
        ElMessage.success('已创建重试任务')
      } catch (error) {
        ElMessage.error(getGenerationErrorMessage(error, '重试失败，请稍后重试'))
      } finally {
        retryingMap.value = {
          ...retryingMap.value,
          [video.id]: false
        }
      }
    }

    // 刷新单个视频数据
    const handleRefreshSingleVideo = async (video) => {
      if (!video) return
      // 已在刷新中，防止重复点击
      if (refreshingMap.value[video.id]) {
        return
      }
      try {
        refreshingMap.value = {
          ...refreshingMap.value,
          [video.id]: true
        }
        const response = await getVideoDetail(video.id)
        if (response.code === 200 && response.data) {
          const item = response.data
          // 更新视频列表中的对应项
          const index = videoList.value.findIndex(v => v.id === video.id)
          if (index !== -1) {
            const previousStatus = videoList.value[index].status
            const updatedVideo = {
              ...videoList.value[index],
              taskId: item.taskId || videoList.value[index].taskId,
              generationId: item.generationId || videoList.value[index].generationId,
              workflowStatus: item.workflowStatus || videoList.value[index].workflowStatus,
              status: normalizeVideoUiStatus(item.status),
              statusText: getStatusText(item.status),
              statusType: getStatusType(item.status),
              progress: item.progress ?? null,
              videoUrl: item.videoUrl || videoList.value[index].videoUrl,
              subtitleRemoval: item.subtitleRemoval !== undefined ? item.subtitleRemoval : videoList.value[index].subtitleRemoval,
              thumbnail: item.thumbnail || videoList.value[index].thumbnail,
              duration: item.seconds !== undefined
                ? (parseInt(item.seconds) || 0)
                : (item.duration || videoList.value[index].duration),
              errorMessage: item.errorMessage || null,
              remark: item.remark != null ? String(item.remark) : (videoList.value[index].remark || ''),
              size: item.size !== undefined ? item.size : videoList.value[index].size,
              platform: item.platform !== undefined ? item.platform : videoList.value[index].platform,
              model: item.model !== undefined ? item.model : videoList.value[index].model,
              prompt: item.prompt !== undefined ? item.prompt : videoList.value[index].prompt,
              inputReference: item.inputReference !== undefined ? item.inputReference : videoList.value[index].inputReference,
              referenceFiles: item.referenceFiles !== undefined ? item.referenceFiles : videoList.value[index].referenceFiles,
              watermark: item.watermark !== undefined ? item.watermark : videoList.value[index].watermark,
              workspaceId: item.workspaceId !== undefined ? item.workspaceId : videoList.value[index].workspaceId,
              seconds: item.seconds !== undefined ? item.seconds : videoList.value[index].seconds,
              isTemp: false
            }
            videoList.value[index] = updatedVideo
            if (previousStatus !== 'failed' && updatedVideo.status === 'failed') {
              updateGenerateCount()
            }
          }
          ElMessage.success('刷新成功')
          loadDailyStats()
        } else {
          ElMessage.error(response.message || '刷新失败')
        }
      } catch (error) {
        ElMessage.error('刷新失败，请稍后重试')
      } finally {
        refreshingMap.value = {
          ...refreshingMap.value,
          [video.id]: false
        }
      }
    }

    // 复制（回填）当前任务的提示词和参考图到生成面板
    const handleCopyToGenerate = async (video) => {
      if (!video) return
      const promptToFill = (video.prompt || video.title || '').toString().trim()
      if (!promptToFill) {
        ElMessage.warning('该任务没有提示词，无法填充')
        return
      }
      generatePrompt.value = promptToFill
      const parsedReferences = parseReferenceFiles(video.referenceFiles)
      const isMultiReferencePlatform = h3VideoPolicy.isH3Platform(video.platform) || wan3ReferencePolicy.isWanPlatform(video.platform) || video.platform === 'seedance' || video.platform === 'seedance-fast'
      const firstImage = parsedReferences?.images?.[0]
      // Restore references saved by older records, including records that only
      // persisted the multi-resource referenceFiles field.
      inputReference.value = referenceDisplayUrl(video.inputReference) || (!isMultiReferencePlatform ? referenceDisplayUrl(firstImage) : '')
      if (video.platform && hasPlatformCode(video.platform)) selectedPlatform.value = video.platform
      // Let platform watchers clear the previous editor before restoring copied references.
      await nextTick()
      // 支持多资源的平台：解析 referenceFiles 回填到资源面板
      if (isMultiReferencePlatform) await generatePanelRef.value?.setSeedanceAssets(parsedReferences || {})
      ElMessage.success('已填充到生成面板')
    }

    // 是否允许编辑备注（所有状态允许；但需要是后端真实记录，而不是临时任务）
    const canEditRemark = (video) => {
      if (!hasPermission('video:update_remark')) return false
      if (!video) return false
      if (video.isTemp) return false
      // 生成任务未拿到真实 id 时，前端会用 Date.now() + Math.random() 作为临时 id（非整数）
      if (typeof video.id === 'number' && !Number.isInteger(video.id)) return false
      return video.id !== null && video.id !== undefined && String(video.id).trim() !== ''
    }

    // 编辑备注
    const handleEditRemark = (video) => {
      if (!canEditRemark(video)) {
        ElMessage.info('任务正在初始化中，请稍后再编辑备注')
        return
      }
      editRemarkForm.value = {
        videoId: video.id,
        remark: video.remark || ''
      }
      editRemarkDialogVisible.value = true
      nextTick(() => {
        editRemarkFormRef.value?.clearValidate()
      })
    }

    // 保存编辑备注
    const handleSaveEditRemark = async () => {
      if (!editRemarkFormRef.value) return
      try {
        await editRemarkFormRef.value.validate()
        updatingRemark.value = true
        const res = await updateVideoRemark(editRemarkForm.value.videoId, editRemarkForm.value.remark?.trim() || '')
        if (res.code === 200) {
          ElMessage.success(res.message || '备注更新成功')
          editRemarkDialogVisible.value = false
          // 更新本地视频列表中的 remark
          const video = videoList.value.find(v => v.id === editRemarkForm.value.videoId)
          if (video) {
            video.remark = editRemarkForm.value.remark?.trim() || ''
          }
        } else {
          ElMessage.error(res.message || '更新备注失败')
        }
      } catch (error) {
        if (error !== false) {
          ElMessage.error('更新备注失败')
        }
      } finally {
        updatingRemark.value = false
      }
    }

    // 取消编辑备注
    const handleCancelEditRemark = () => {
      editRemarkDialogVisible.value = false
      editRemarkForm.value = {
        videoId: null,
        remark: ''
      }
      if (editRemarkFormRef.value) {
        editRemarkFormRef.value.clearValidate()
      }
    }

    // 判断视频方向并返回对应的 CSS 类名
    const getVideoOrientationClass = (video) => {
      if (!video.size) {
        // 如果没有尺寸信息，默认使用横版
        return 'video-landscape'
      }
      
      // 解析尺寸字符串，如 "1280x720" 或 "720x1280"
      const sizeMatch = video.size.match(/(\d+)x(\d+)/)
      if (sizeMatch) {
        const width = parseInt(sizeMatch[1])
        const height = parseInt(sizeMatch[2])
        const aspectRatio = width / height
        
        // 如果宽高比大于1，是横版；小于1，是竖版；等于1，是正方形
        if (aspectRatio > 1) {
          return 'video-landscape'
        } else if (aspectRatio < 1) {
          return 'video-portrait'
        } else {
          return 'video-square'
        }
      }
      
      // 默认横版
      return 'video-landscape'
    }

    // 格式化时长（秒数转字符串）
    const formatDuration = (seconds) => {
      if (!seconds) return '0:00'
      const secs = parseInt(seconds)
      const hours = Math.floor(secs / 3600)
      const minutes = Math.floor((secs % 3600) / 60)
      const sec = secs % 60
      if (hours > 0) {
        return `${hours}:${String(minutes).padStart(2, '0')}:${String(sec).padStart(2, '0')}`
      }
      return `${minutes}:${String(sec).padStart(2, '0')}`
    }

    // 加载视频列表
    const loadVideoList = (isLoadMore = false) => {
      // 如果没有选中工作空间，后端要求必须传 workspaceId，直接清空列表并返回，避免 500 错误
      if (!currentWorkspaceId.value) {
        if (isLoadMore) {
          loadingMore.value = false
        } else {
          loading.value = false
          currentPage.value = 1
          hasMore.value = false
          videoList.value = []
          total.value = 0
        }
        return
      }
      if (isLoadMore) {
        loadingMore.value = true
      } else {
        loading.value = true
        // 重置分页与数据
        currentPage.value = 1
        hasMore.value = true
        videoList.value = []
      }
      const pageToLoad = currentPage.value
      const queryParams = {
        current: pageToLoad,
        size: pageSize.value,
        workspaceId: currentWorkspaceId.value
      }
      getVideoRecords(queryParams)
        .then((response) => {
          if (response.code === 200 && response.data) {
            const { records, total: totalCount } = response.data
            
            // 映射API数据到组件需要的格式
            const newVideos = records.map((item) => ({
              id: item.id,
              taskId: item.taskId,
              generationId: item.generationId,
              workflowStatus: item.workflowStatus,
              title: item.prompt || `视频 #${item.id}`,
              prompt: item.prompt,
              author: `用户 ${item.userId}`,
              thumbnail: getThumbnail(item.videoUrl, item.status),
              videoUrl: item.videoUrl,
              subtitleRemoval: item.subtitleRemoval || null,
              duration: parseInt(item.seconds) || 0,
              seconds: item.seconds,
              status: normalizeVideoUiStatus(item.status),
              statusText: getStatusText(item.status),
              statusType: getStatusType(item.status),
              progress: item.progress ?? null,
              platform: item.platform,
              model: item.model,
              size: item.size,
              watermark: item.watermark || false,
              inputReference: item.inputReference,
              referenceFiles: item.referenceFiles || null,
              workspaceId: item.workspaceId,
              createTime: item.createdAt,
              updateTime: item.updatedAt,
              errorMessage: item.errorMessage,
              remark: item.remark != null ? String(item.remark) : '',
              // 来自后端接口的数据都是已经落库的正式记录
              isTemp: false
            })).filter(v => !isFilteredModel(v.model))

            if (isLoadMore) {
              // 追加数据
              videoList.value = [...videoList.value, ...newVideos]
            } else {
              // 替换数据
              videoList.value = newVideos
            }
            
            syncSubmissionCards()
            total.value = totalCount || 0
            
            // 判断是否还有更多数据
            hasMore.value = videoList.value.length < total.value
            
            // 确保定时更新器正在运行（定时器内部会检查是否有待处理的视频）
            if (!statusUpdateTimer.value) {
              startStatusUpdate()
            }
            
            // 如果还有数据，页码加1，准备下次加载
            if (hasMore.value) {
              currentPage.value = pageToLoad + 1
            }
          } else {
            ElMessage.error(response.message || '获取视频列表失败')
          }
        })
        .catch(() => {
          // 错误信息已在拦截器中处理
        })
        .finally(() => {
          loading.value = false
          loadingMore.value = false
        })
    }

    // 加载更多数据
    const loadMore = () => {
      if (!hasMore.value || loadingMore.value || loading.value) {
        return
      }
      loadVideoList(true)
    }

    // 修改点：滚动事件处理（使用元素的 scroll 事件）
    const handleScroll = (e) => {
      const target = e.target
      if (!target) return
      
      // 记录当前滚动位置
      scrollPosition.value = target.scrollTop

      const scrollTop = target.scrollTop
      const clientHeight = target.clientHeight
      const scrollHeight = target.scrollHeight
      
      // 当滚动到距离底部 200px 时，触发加载更多
      if (scrollTop + clientHeight >= scrollHeight - 200) {
        loadMore()
      }
    }

// Sidebar 通知密码修改成功后的处理：刷新本地用户信息
const handlePasswordChanged = () => {
  userInfo.value = getUserInfo()
}

// Sidebar 通知用户信息刷新后的处理：同步本地用户信息
const handleUserInfoRefreshed = (updatedUserInfo) => {
  // 检测是否是用户切换（用户信息发生变化）
  const isUserSwitched = updatedUserInfo &&
    (!userInfo.value ||
     userInfo.value.userId !== updatedUserInfo.userId ||
     userInfo.value.username !== updatedUserInfo.username)

  if (updatedUserInfo) {
    userInfo.value = updatedUserInfo
  } else {
    userInfo.value = getUserInfo()
  }

  // 用户切换时，清除旧用户的脚本选择缓存
  if (isUserSwitched) {
    localStorage.removeItem(SELECTED_SCRIPT_KEY)
  }

  // 用户切换时重新加载剧本数据
  if (isUserSwitched) {
    reloadScripts()
  }
}

// Sidebar 通知头像更新后的处理：同步本地用户信息
    const handleAvatarChange = (updatedUserInfo) => {
      if (updatedUserInfo) {
        setUserInfo(updatedUserInfo)
        userInfo.value = updatedUserInfo
      } else {
        userInfo.value = getUserInfo()
      }
    }
    
    const getFriendlyErrorMessage = (errorMessage) => {
      if (!errorMessage) return null
      const lower = errorMessage.toLowerCase()
      if (lower.includes('face') || lower.includes('real person')) return '似乎包含了未审核的真人，把图片上传到资产库再重试吧～'
      if (lower.includes('sensitive information')) return '内容好像触碰到了一些敏感词，改改提示词再试试吧～'
      if (lower.includes('copyright restrictions') || lower.includes('copyright')) return '内容可能有版权方面的限制，换个描述方式再试试？'
      return null
    }

    // 本地失败详情弹窗（审核未过、未落库的任务）
    const showLocalErrorDialog = ref(false)
    const localErrorVideo = ref(null)

    // 点击视频卡片
    const handleVideoClick = (video) => {
      if (!video || !video.id) return
      // 进行中的临时任务（尚未落库）不进入详情
      if (video.isTemp && video.status !== 'failed') {
        ElMessage.info('任务正在初始化中，请稍后再查看详情')
        return
      }
      // 未落库的失败任务（浮点临时 ID）直接展示内存数据，不请求后端
      const isTempFloatId = typeof video.id === 'number' && !Number.isInteger(video.id)
      if (isTempFloatId) {
        localErrorVideo.value = video
        showLocalErrorDialog.value = true
        return
      }
      currentDetailVideoId.value = video.id
      showDetailOverlay.value = true
    }

    // 关闭详情覆盖层
    const handleCloseDetailOverlay = () => {
      showDetailOverlay.value = false
      currentDetailVideoId.value = null
    }

    // 删除视频
    const handleDelete = async (video) => {
      try {
        await ElMessageBox.confirm(
          '<div class="delete-dialog-content"><div class="delete-icon-wrapper"><svg class="delete-icon-large" viewBox="0 0 1024 1024" xmlns="http://www.w3.org/2000/svg"><path d="M360 184h-8c4.4 0 8-3.6 8-8v8h304v-8c0 4.4 3.6 8 8 8h-8v72h72v-80c0-35.3-28.7-64-64-64H352c-35.3 0-64 28.7-64 64v80h72v-72zm504 72H160c-17.7 0-32 14.3-32 32v32c0 4.4 3.6 8 8 8h60.4l24.7 523c1.6 34.1 29.8 61 63.9 61h454c34.1 0 62.3-26.9 63.9-61l24.7-523H896c4.4 0 8-3.6 8-8v-32c0-17.7-14.3-32-32-32zM731.3 840H292.7l-24.2-512h487l-24.2 512z" fill="#FF4D4F"/></svg></div><div class="delete-message">删除后无法恢复，确定要删除这个视频吗？</div></div>',
          '确认删除该视频？',
          {
            confirmButtonText: '确定删除',
            cancelButtonText: '取消',
            type: 'warning',
            center: true,
            dangerouslyUseHTMLString: true,
            distinguishCancelAndClose: true,
            customClass: 'delete-confirm-dialog',
            confirmButtonClass: 'delete-confirm-button',
            cancelButtonClass: 'delete-cancel-button'
          }
        )
        // 用户确认后执行删除
        const response = await deleteVideo(video.id)
        if (response.code === 200) {
          ElMessage.success('删除成功')
          // 从列表中移除该视频
          const index = videoList.value.findIndex(v => v.id === video.id)
          if (index !== -1) {
            videoList.value.splice(index, 1)
            total.value = total.value - 1
          }
          loadDailyStats()
        } else {
          ElMessage.error(response.message || '删除失败')
        }
      } catch (error) {
        // 用户点击取消或关闭对话框时，error 为 'cancel'，不显示错误
        if (error !== 'cancel' && error !== 'close') {
          ElMessage.error('删除失败: ' + (error.message || '未知错误'))
        }
      }
    }

    // 生成视频
    const handleGenerate = async (payload) => {
      // 防止重复提交：如果正在生成中，直接返回
      if (generating.value) {
        return
      }
      if (!platformAvailable.value) {
        ElMessage.warning(selectedPlatform.value ? '当前模型已停用，请选择其他模型' : '暂无可用模型')
        return
      }
      // 使用子组件传递的数据，如果没有则使用本地状态（向后兼容）
      const promptPart = (payload?.prompt || generatePrompt.value || '').toString().trim()
      const remarkPart = (payload?.remark || generateRemark.value || '').trim()
      if (!promptPart) {
        ElMessage.warning('请输入视频描述')
        return
      }
      // 规范化批次数（最小1，最大5）
      const referenceError = h3VideoPolicy.getReferenceError(selectedPlatform.value, payload)
        || wan3ReferencePolicy.getWanReferenceError(selectedPlatform.value, payload)
      if (referenceError) {
        ElMessage.warning(referenceError)
        return
      }
      let normalizedBatchCount = Math.min(5, Math.max(1, Number(generateBatchCount.value) || 1))

      // 根据 platformCode 找到对应平台，判断 platformName 是否为 FAST
      const currentPlatform = platformOptions.value.find(p => p.platformCode === selectedPlatform.value)
      const isFastPlatform = selectedPlatform.value === 'seedance-fast' || currentPlatform?.platformName === 'FAST'

      // FAST 平台只支持单条生成
      if (isFastPlatform) {
        normalizedBatchCount = 1
      }

      if (generateBatchCount.value !== normalizedBatchCount) {
        generateBatchCount.value = normalizedBatchCount
      }
      // 校验剩余生成次数是否充足
      const remainingCount = userInfo.value?.generateCount
      if (typeof remainingCount === 'number' && remainingCount < normalizedBatchCount) {
        ElMessage.warning(`剩余生成次数不足，当前剩余 ${remainingCount} 次`)
        return
      }
      // 如果为批量生成（大于1次）且非 FAST 平台，进行二次确认
      if (normalizedBatchCount > 1 && !isFastPlatform) {
        try {
          await ElMessageBox.confirm(
            `<div style="font-size:14px;color:#1f2937;">本次将创建 <strong style="color:#2563eb;">${normalizedBatchCount}</strong> 条生成任务，是否继续？</div>`,
            '确认批量生成',
            {
              confirmButtonText: '确认生成',
              cancelButtonText: '取消',
              type: 'warning',
              closeOnClickModal: false,
              dangerouslyUseHTMLString: true
            }
          )
        } catch (err) {
          // 用户取消
          return
        }
      }
      // 确认批量生成期间模型也可能被停用。
      if (!platformAvailable.value) {
        ElMessage.warning('当前模型已停用，请选择其他模型')
        return
      }
      generating.value = true
      // 获取当前工作空间的系统提示词
      const workspaceSystemPrompt = currentWorkspaceSystemPrompt.value?.trim?.() || ''

      // 直接使用用户输入的描述
      let finalPrompt = promptPart
      
      // 如果工作空间设置了系统提示词，则拼接在前面
      if (workspaceSystemPrompt) {
        finalPrompt = workspaceSystemPrompt + ', ' + finalPrompt
      }
      const generateData = {
        prompt: finalPrompt,
        seconds: generateSeconds.value,
        size: generateSize.value,
        platform: selectedPlatform.value || undefined,
        workspaceId: currentWorkspaceId.value || undefined
      }
      // 如果输入了备注，透传 remark 字段
      if (remarkPart) {
        generateData.remark = remarkPart
      }
      // 如果有上传的图片，添加 inputReference 参数
      if (inputReference.value && !h3VideoPolicy.isH3Platform(selectedPlatform.value)) {
        generateData.inputReference = inputReference.value
      }
      // Seedance 平台：固定模型 + 透传多资源数据
      if (selectedPlatform.value === 'seedance') {
        generateData.model = 'ep-20260407113743-b8w5s'
      }
      if (wan3ReferencePolicy.isWanPlatform(selectedPlatform.value)) {
        generateData.model = 'wan3.0-video'
      }
      if (h3VideoPolicy.isH3Platform(selectedPlatform.value)) {
        generateData.model = h3VideoPolicy.MODEL
      }
      // Seedance Fast 平台：Fast 模型 + 透传多资源数据
      if (selectedPlatform.value === 'seedance-fast') {
        generateData.model = 'ep-20260417185323-2cpl7'
      }
      if (payload?.images?.length) {
        generateData.images = payload.images.map(i => ({ url: i.url, role: i.role, volcAssetId: i.volcAssetId }))
      }
      if (payload?.videos?.length) {
        generateData.videos = payload.videos.map(v => ({ url: v.url, duration: v.duration, volcAssetId: v.volcAssetId }))
      }
      if (payload?.audios?.length) {
        generateData.audios = payload.audios.map(a => ({ url: a.url, duration: a.duration, volcAssetId: a.volcAssetId }))
      }
      try {
        await enqueueVideoSubmissions(generateData, payload, normalizedBatchCount)
        syncSubmissionCards()
        generatePrompt.value = ''
        generateRemark.value = ''
        inputReference.value = ''
        generatePanelRef.value?.resetAfterGenerateSuccess?.()
        startStatusUpdate()
        ElMessage.success('任务已提交，参考文件将在后台上传')
      } catch (error) {
        ElMessage.error(getGenerationErrorMessage(error))
      } finally {
        generating.value = false
      }
    }

    const syncSubmissionCards = () => {
      for (const task of videoSubmissions.value) {
        if (String(task.request.workspaceId) !== String(currentWorkspaceId.value)) continue
        const index = videoList.value.findIndex(v => v.generationId === task.id || v.taskId === task.id)
        if (task.state === 'SUBMITTED' && index < 0) continue
        if (task.state === 'CANCELLED') { if (index >= 0) videoList.value.splice(index, 1); continue }
        const server = task.server || {}
        const old = index >= 0 ? videoList.value[index] : {}
        if (task.state === 'SUBMITTED' && !old.submissionState && !old.isTemp) continue
        const pending = task.state !== 'SUBMITTED'
        const card = { ...task.request, ...old,
          ...submissionReferenceSnapshot(task),
          id: server.id || old.id || task.id, generationId: task.id,
          taskId: server.taskId || task.id, title: task.request.prompt, prompt: task.request.prompt,
          status: pending ? 'in_progress' : normalizeVideoUiStatus(old.status === 'completed' ? old.status : server.status),
          duration: parseInt(task.request.seconds) || 0, createTime: task.createdAt,
          videoUrl: old.videoUrl || server.videoUrl, errorMessage: task.error || server.errorMessage,
          workflowStatus: pending ? task.state : (server.workflowStatus || 'SUBMITTED'),
          submissionState: pending ? task.state : null,
          submissionText: pending ? submissionLabels[task.state] : '',
          statusText: pending ? submissionLabels[task.state] : getStatusText(server.status),
          isTemp: !server.id }
        if (index >= 0) videoList.value[index] = card
        else videoList.value.unshift(card)
      }
    }
    watch(videoSubmissions, syncSubmissionCards)
    const handleRetrySubmission = async (video) => {
      try { await retryVideoSubmission(video.generationId) } catch (e) { ElMessage.error(e.message) }
    }

    // 更新生成次数
    const updateGenerateCount = async () => {
      try {
        const response = await getGenerateCount()
        if (response.code === 200 && response.data) {
          // 响应格式: { code: 200, message: "查询成功", data: { generateCount: 10 } }
          const count = response.data.generateCount
          if (count !== undefined) {
            const updatedUserInfo = {
              ...userInfo.value,
              generateCount: count
            }
            setUserInfo(updatedUserInfo)
            userInfo.value = updatedUserInfo
          }
        }
      } catch (error) {
        // 静默失败，不影响主流程
      }
    }

    // 定时更新视频状态
    const statusUpdateTimer = ref(null)
    // 每个视频的连续失败次数记录
    const videoFailureCount = ref(new Map())
    const progressRequests = new Set()

    const updateVideoStatus = async () => {
      // 找出需要更新的视频（状态不是 completed 和 failed）
      // 只要有 taskId 就可以更新（包括新添加的临时视频）
      const polledWorkspaceId = currentWorkspaceId.value
      const videosToUpdate = videoList.value.filter(
        video => video.status !== 'completed' && video.status !== 'failed' && video.taskId && !video.isTemp
      )
      if (videosToUpdate.length === 0) {
        // 如果没有需要更新的视频，清除定时器
        if (statusUpdateTimer.value) {
          clearInterval(statusUpdateTimer.value)
          statusUpdateTimer.value = null
        }
        return
      }
      // 批量更新视频状态
      const updatePromises = videosToUpdate.map(async (video) => {
        if (progressRequests.has(video.taskId)) return
        progressRequests.add(video.taskId)
        try {
          // 使用静默模式进行轮询请求，避免频繁弹出错误提示
          // Seedance 和 Wan 系列平台使用 GET 方式查询进度，其他平台使用 POST
          const progressFn = video.generationId
            ? () => getVideoGenerationStatus(video.generationId)
            : (video.platform === 'seedance' || video.platform === 'seedance-fast' || wan3ReferencePolicy.isWanPlatform(video.platform) || h3VideoPolicy.isH3Platform(video.platform)
            ? () => getSeedanceProgress(video.taskId)
            : () => getVideoProgress(video.taskId))
          const response = await withSilentMode(progressFn)
          if (response.code === 200 && response.data) {
            // 请求成功，重置失败计数
            videoFailureCount.value.delete(video.taskId)

            const item = response.data
            // 更新视频列表中的对应项（通过 taskId 查找，因为可能还没有真实 ID）
            if (String(polledWorkspaceId) !== String(currentWorkspaceId.value)) return
            const index = videoList.value.findIndex(v => v.taskId === video.taskId)
            if (index !== -1) {
              const previousStatus = videoList.value[index].status
              if (previousStatus === 'completed' && normalizeVideoUiStatus(item.status) !== 'completed') return
              // 如果后端返回了真实ID，更新它（替换临时ID）
              const updatedVideo = {
                ...videoList.value[index],
                taskId: item.taskId || videoList.value[index].taskId,
                workflowStatus: item.workflowStatus || videoList.value[index].workflowStatus,
                status: normalizeVideoUiStatus(item.status),
                statusText: getStatusText(item.status),
                statusType: getStatusType(item.status),
                progress: item.progress ?? null,
                videoUrl: item.videoUrl || videoList.value[index].videoUrl,
                subtitleRemoval: item.subtitleRemoval !== undefined ? item.subtitleRemoval : videoList.value[index].subtitleRemoval,
                errorMessage: item.errorMessage || null,
                // 备注允许为空字符串，不能用 `||` 吞掉后端的空值
                remark: item.remark != null ? String(item.remark) : (videoList.value[index].remark || ''),
                // 一旦后端返回了最新进度数据，就视为已落库的正式记录
                isTemp: false
              }
              // 如果后端返回了真实ID，更新它
              if (item.id) {
                updatedVideo.id = item.id
              }
              // 如果后端返回了其他字段，也更新它们
              if (item.size !== undefined) {
                updatedVideo.size = item.size
              }
              if (item.platform !== undefined) {
                updatedVideo.platform = item.platform
              }
              if (item.model !== undefined) {
                updatedVideo.model = item.model
              }
              if (item.prompt !== undefined) {
                updatedVideo.prompt = item.prompt
                updatedVideo.title = item.prompt || updatedVideo.title
              }
              if (item.inputReference !== undefined) {
                updatedVideo.inputReference = item.inputReference
              }
              if (item.referenceFiles !== undefined) {
                updatedVideo.referenceFiles = item.referenceFiles
              }
              if (item.watermark !== undefined) {
                updatedVideo.watermark = item.watermark
              }
              if (item.workspaceId !== undefined) {
                updatedVideo.workspaceId = item.workspaceId
              }
              if (item.seconds !== undefined) {
                updatedVideo.duration = parseInt(item.seconds) || 0
                updatedVideo.seconds = item.seconds
              }
              videoList.value[index] = updatedVideo
              syncSubmissionCards()
              if (previousStatus !== 'failed' && updatedVideo.status === 'failed') {
                updateGenerateCount()
                loadDailyStats()
              }

              if (updatedVideo.status === 'completed' && updatedVideo.videoUrl) {
                updatedVideo.thumbnail = getThumbnail(updatedVideo.videoUrl)
              }
            }
          }
        } catch (error) {
          // 静默模式下错误不会弹窗，只在控制台记录
          // 记录失败次数，连续失败多次时给用户一个温和的提示
          const failCount = (videoFailureCount.value.get(video.taskId) || 0) + 1
          videoFailureCount.value.set(video.taskId, failCount)

          // 连续失败 30 次（约 30 秒）时，显示一次提示，然后重置计数
          if (failCount >= 30) {
            ElMessage.warning({
              message: '网络不稳定，视频状态更新延迟，请稍候...',
              duration: 3000,
              showClose: true
            })
            videoFailureCount.value.set(video.taskId, 0)
          }
        } finally {
          progressRequests.delete(video.taskId)
        }
      })
      await Promise.all(updatePromises)
    }

    const startStatusUpdate = () => {
      // 清除已存在的定时器
      if (statusUpdateTimer.value) {
        clearInterval(statusUpdateTimer.value)
        statusUpdateTimer.value = null
      }
      // 每1秒更新一次
      // 使用 requestAnimationFrame 确保在页面可见时执行
      statusUpdateTimer.value = setInterval(() => {
        // 检查页面是否可见
        if (document.hidden) {
          return
        }
        updateVideoStatus()
      }, 1000)
    }

    const stopStatusUpdate = () => {
      if (statusUpdateTimer.value) {
        clearInterval(statusUpdateTimer.value)
        statusUpdateTimer.value = null
      }
    }

    // 页面可见性变化处理
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        // 页面变为可见时，立即执行一次更新
        updateVideoStatus()
        loadDailyStats()
        // 确保定时器正在运行
        if (!statusUpdateTimer.value) {
          startStatusUpdate()
        }
      }
    }

    onMounted(async () => {
      startPlatformRefresh()
      // 监听页面可见性变化
      updateViewportState()
      document.addEventListener('visibilitychange', handleVisibilityChange)
      window.addEventListener('resize', updateViewportState)
      // 并行发起 scripts 和 platformConfigs 请求，减少串行等待时间
      const [scriptsRes] = await Promise.all([
        getScripts({ includeImageCover: true }).catch(() => null),
        loadPlatformConfigs()
      ])

      // 兼容后端可能返回数组或标准 { code, data } 的多种格式
      let scriptsArray = []
      if (!scriptsRes) {
        scriptsArray = []
        scriptsError.value = '剧本加载失败，请重试'
      } else if (Array.isArray(scriptsRes)) {
        scriptsArray = scriptsRes
      } else if (Array.isArray(scriptsRes.data)) {
        scriptsArray = scriptsRes.data
      } else if (Array.isArray(scriptsRes.data?.data)) {
        scriptsArray = scriptsRes.data.data
      }

      preloadedScripts.value = scriptsArray

      try {
        if (scriptsArray.length === 0) {
          localStorage.removeItem(SELECTED_SCRIPT_KEY)
        }
      } catch (e) {
        // ignore localStorage 写入错误
      }

      // 标记剧本加载结束，允许受依赖的子组件渲染
      if (!scriptsError.value && route.params.scriptId && !scriptsArray.some(item => String(item.id) === String(route.params.scriptId))) {
        router.replace({ name: 'VideoList' })
      }
      scriptsLoaded.value = true
      nextTick(() => {
        scheduleUpdateDisplayVideosPerRow()
        if (window.ResizeObserver && videoContentRef.value) {
          videoContentResizeObserver = new ResizeObserver(scheduleUpdateDisplayVideosPerRow)
          videoContentResizeObserver.observe(videoContentRef.value)
        }
      })
      syncCurrentScriptId()
      loadDailyStats()
      // 注意：已移除 window scroll 监听，改为 template 中 @scroll 绑定
      // 状态更新定时器由 loadVideoList 在数据加载完成后按需启动

      // 启动视频上传定时任务（每5秒执行一次，最多5个并发线程）
      setUpdateCallback(async (updateMap) => {
        // 更新list页面中对应数据的视频链接
        for (const video of videoList.value) {
          if (updateMap.has(video.videoUrl)) {
            const oldUrl = video.videoUrl
            const newUrl = updateMap.get(video.videoUrl)

            // 删除 IndexedDB 中的旧缓存（防止刷新后重新缓存旧URL）
            try {
              await deleteCachedVideo(oldUrl)
            } catch (e) {
              // ignore
            }

            // 更新 videoUrl
            video.videoUrl = newUrl

            // 同时清除内存中缓存的blob URL
            if (cachedVideoUrls.value[video.id]) {
              URL.revokeObjectURL(cachedVideoUrls.value[video.id])
              // 使用解构创建新对象来触发 Vue 响应式更新（delete 操作符不会触发更新）
              // eslint-disable-next-line no-unused-vars
              const { [video.id]: removed, ...rest } = cachedVideoUrls.value
              cachedVideoUrls.value = rest
            }
          }
        }
      })

      // 启动视频上传定时任务（5秒间隔，最多2个并发线程，七牛云直传）
      startVideoUploadTask(5000)
    })

    // keep-alive 组件重新激活时，恢复滚动位置
    let wasDeactivated = false
    onActivated(async () => {
      if (!wasDeactivated || !scriptsLoaded.value) return
      wasDeactivated = false
      syncCurrentScriptId()
      startPlatformRefresh()
      loadPlatformConfigs()
      userInfo.value = getUserInfo()
      loadDailyStats()
      await nextTick()
      if (videoContentRef.value) videoContentRef.value.scrollTop = scrollPosition.value
      scheduleUpdateDisplayVideosPerRow()
      updateVideoStatus()
    })

    onDeactivated(() => {
      wasDeactivated = true
      stopPlatformRefresh()
      Object.values(videoRefs.value).forEach(video => video?.pause())
    })

    onUnmounted(() => {
      platformConfigsDisposed = true
      stopPlatformRefresh()
      // 移除页面可见性监听
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      window.removeEventListener('resize', updateViewportState)
      if (updateDisplayVideosPerRowRaf) {
        cancelAnimationFrame(updateDisplayVideosPerRowRaf)
        updateDisplayVideosPerRowRaf = null
      }
      if (videoContentResizeObserver) {
        videoContentResizeObserver.disconnect()
        videoContentResizeObserver = null
      }
      // 清除状态更新定时器
      stopStatusUpdate()
      // 停止视频上传定时任务
      stopVideoUploadTask()
      // 清理所有视频播放状态
      Object.values(videoRefs.value).forEach((videoEl) => {
        if (videoEl && document.contains(videoEl)) {
          try {
            videoEl.pause()
            videoEl.src = ''
            videoEl.load()
          } catch (error) {
            // 忽略错误
          }
        }
      })
      // 释放所有 blob URL
      Object.values(cachedVideoUrls.value).forEach((url) => {
        if (url && url.startsWith('blob:')) {
          URL.revokeObjectURL(url)
        }
      })
      // 清空视频引用和缓存 URL
      videoRefs.value = {}
      cachedVideoUrls.value = {}
    })

    return {
      Loading,
      loading,
      loadingMore,
      hasMore,
      generating,
      generatePrompt,
      generateRemark,
      generateSeconds,
      generateSize,
      generateModel,
      generateBatchCount,
      videoList,
      hasVideoProgress,
      formatDuration,
      formatDurationLabel,
      todayGeneratedDurationSeconds,
      todayGeneratedCount,
      todayDateText,
      dailyStats,
      dailyStatsLoading,
      dailyStatsDialogVisible,
      handleOpenDailyStats,
      hasPlatformCode,
      getPlatformName,
      getPlatformTagClass,
      handleVideoClick,
      handleDelete,
      handleGenerate,
      handleRetrySubmission,
      workflowLabel: (state, status, platform) => (platform === 'wan3-cd' ? wan3CdWorkflowLabel(state, status) : '')
        || ({ ...submissionLabels, ACCEPTED: '等待上传', UPLOAD_FAILED: '参考准备失败' })[state],
      handleVideoLoaded,
      getVideoOrientationClass,
      setVideoRef,
      hoveredVideoId, playingVideoId, safePlayVideo,
      handleMouseEnter,
      handleMouseLeave,
      Close,
      userInfo,
      handleAvatarChange,
      inputReference,
      generatePanelRef,
      handlePasswordChanged,
      handleUserInfoRefreshed,
      cachedVideoUrls,
      displayVideoUrl,
      getThumbnail,
      isCleanedVideo,
      handleSubtitleChange,
      handleDownload,
      Download,
      downloadingMap,
      RefreshRight,
      Refresh,
      retryingMap,
      handleRetry,
      refreshingMap,
      handleRefreshSingleVideo,
      CopyDocument,
      handleCopyToGenerate,
      Edit,
      sidebarCollapsed,
      generatePanelWidth,
      generatePanelWidthStyle,
      isGeneratePanelResizing,
      route,
      scriptsLoaded,
      scriptsError,
      preloadedScripts,
      openStudioScript,
      reloadScripts,
      toggleSidebar,
      editRemarkDialogVisible,
      editRemarkForm,
      editRemarkFormRef,
      editRemarkRules,
      updatingRemark,
      canEditRemark,
      handleEditRemark,
      handleSaveEditRemark,
      handleCancelEditRemark,
      platformOptions,
      selectedPlatform,
      platformAvailable,
      currentWorkspaceId,
      currentScriptId,
      currentWorkspaceSystemPrompt,
      handleWorkspaceSelected,
      sidebarRef,
      handleVideoError,
      handleCacheCleaned,
      showDetailOverlay,
      currentDetailVideoId,
      handleCloseDetailOverlay,
      showLocalErrorDialog,
      localErrorVideo,
      getFriendlyErrorMessage,
      getGenerationErrorMessage,
      // 新增返回
      videoContentRef,
      handleScroll,
      Top,
      videosPerRow,
      displayVideosPerRow,
      videosPerRowOptions,
      handleVideosPerRowChange,
      handleGeneratePanelResize,
      backtopBottom,
      backtopRight
    }
  }
}
</script>

<style scoped>
.video-list-container {
  --navigation-duration: 360ms;
  --navigation-easing: cubic-bezier(0.22, 1, 0.36, 1);
  --navigation-sidebar-width: 250px;
  min-height: 100vh;
  background: #F5F7FA;
  position: relative;
  overflow-x: hidden;
  /* 修改点：固定高度并隐藏溢出，防止出现双重滚动条 */
  height: 100vh;
  overflow: hidden;
}

.video-studio-shell { height: 100%; }
.video-studio-load-state {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 14px;
  height: 100%;
  color: #777;
  background: #000;
}
.video-studio-load-state p { margin: 0; }
.video-studio-load-state .studio-retry {
  padding: 7px 14px;
  border: 1px solid #555;
  border-radius: 16px;
  color: #ddd;
  background: #222;
  cursor: pointer;
}

/* 覆盖层：VideoDetail 盖在列表之上，而不销毁列表页面 */
.video-detail-overlay {
  position: fixed;
  inset: 0;
  z-index: 3000;
  display: flex;
  align-items: stretch;
  justify-content: stretch;
  background: rgba(0, 0, 0, 0.4);
  overflow: hidden;
}

/* 确保 VideoDetail 组件在覆盖层中占满全屏 */
.video-detail-overlay :deep(.video-detail-container) {
  width: 100%;
  height: 100%;
  min-height: 100vh;
  max-height: 100vh;
  overflow-y: auto;
  overflow-x: hidden;
}

.video-detail-fade-enter-active,
.video-detail-fade-leave-active {
  transition: opacity 0.25s ease;
}

.video-detail-fade-enter-from,
.video-detail-fade-leave-to {
  opacity: 0;
}

/* 主布局：左右分栏 L型布局 */
.main-layout {
  display: flex;
  height: 100vh; /* 修改点：固定高度 */
  overflow: hidden;
}

/* 右侧内容区 */
.content-area {
  flex: 1;
  margin-left: var(--navigation-sidebar-width);
  min-width: 0;
  display: flex;
  flex-direction: column;
  height: 100vh; /* 修改点：固定高度 */
  overflow: hidden;
  transition: margin-left var(--navigation-duration) var(--navigation-easing);
}

.navigation-collapsed .content-area {
  margin-left: 0;
}

.video-list-container :deep(.sidebar) {
  transition: transform var(--navigation-duration) var(--navigation-easing);
}

.navigation-collapsed :deep(.sidebar) {
  transform: translateX(-100%);
  pointer-events: none;
}

.video-list-container :deep(.video-header) {
  transition: transform var(--navigation-duration) var(--navigation-easing),
    opacity var(--navigation-duration) var(--navigation-easing);
}

.navigation-collapsed :deep(.video-header) {
  transform: translateY(-100%);
  opacity: 0;
  pointer-events: none;
}

.navigation-toggle {
  position: fixed;
  left: var(--navigation-sidebar-width);
  top: 50%;
  transform: translateY(-50%);
  z-index: 1003;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 56px;
  padding: 0;
  border: 1px solid #cbd8e5;
  border-left: 0;
  border-radius: 0 8px 8px 0;
  background: #fff;
  color: #526779;
  box-shadow: 2px 0 8px rgb(44 75 105 / 10%);
  cursor: pointer;
  transition: left var(--navigation-duration) var(--navigation-easing),
    background-color 160ms ease, color 160ms ease;
}

.navigation-toggle:hover {
  background: #edf6ff;
  color: #1875f0;
}

.navigation-toggle:focus-visible {
  outline: 2px solid #1875f0;
  outline-offset: 2px;
}

.navigation-toggle .el-icon {
  transition: transform var(--navigation-duration) var(--navigation-easing);
}

.navigation-collapsed .navigation-toggle {
  left: 0;
}

.navigation-collapsed .navigation-toggle .el-icon {
  transform: rotate(180deg);
}

/* 登录中加载动画 */
.login-loading-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}

.login-loading-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 20px;
}

.login-loading-icon {
  font-size: 48px;
  color: #1875F0;
  animation: rotate 1s linear infinite;
}

.login-loading-text {
  font-size: 18px;
  font-weight: 500;
  color: #1A2B45;
  letter-spacing: 2px;
}

@keyframes rotate {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

  /* 修改点：视频内容区域样式调整 */
.video-content {
  flex: 1;
  padding: 24px;
  padding-top: 70px; /* HeaderBar 固定定位高度 64px + 额外间距 6px，避免遮挡第一行数据 */
  padding-right: calc(var(--generate-panel-width, 392px) + 24px);
  padding-bottom: 32px;

  /* 核心修改：设置高度并开启垂直滚动 */
  height: 100%; /* 相对于父容器 flex 剩余空间 */
  overflow-y: auto;
  overflow-x: hidden;

  /* 视频列表整体背景色（改为浅灰白色，保持与整体页面一致） */
  background: #f5f7fa;

  /* 自定义滚动条样式 - 圆滑透明 */
  scrollbar-width: thin;
  scrollbar-color: rgba(0, 0, 0, 0.3) transparent;

  position: relative;
  min-width: 0;
  box-sizing: border-box;
  transition: padding-right 0.22s ease,
    padding-top var(--navigation-duration) var(--navigation-easing);
}

.navigation-collapsed .video-content {
  padding-top: 24px;
}

.collapsed-studio-navigation {
  margin-bottom: 12px;
}

.collapsed-studio-navigation .el-button {
  height: 36px;
  padding: 0 8px;
}

.collapsed-studio-navigation .el-icon {
  margin-right: 6px;
}

@media (min-width: 901px) {
  .video-list-container :deep(.generate-container) {
    transition: width 0.22s ease, box-shadow 0.2s ease,
      top var(--navigation-duration) var(--navigation-easing);
  }

  .navigation-collapsed :deep(.generate-container) {
    top: 0;
  }

  .video-list-container :deep(.generate-container.is-resizing) {
    transition: none;
  }
}

.video-list-container.generate-panel-resizing .video-content {
  transition: none;
}

/* video-content 滚动条样式 */
.video-content::-webkit-scrollbar {
  width: 8px;
}

.video-content::-webkit-scrollbar-track {
  background: transparent;
}

.video-content::-webkit-scrollbar-thumb {
  background: rgba(0, 0, 0, 0.3);
  border-radius: 4px;
  transition: background 0.3s ease;
}

.video-content::-webkit-scrollbar-thumb:hover {
  background: rgba(0, 0, 0, 0.5);
}

.content-main-with-sidebar {
  display: flex;
  align-items: flex-start;
  gap: 16px;
  flex: 1; /* 修改点：让其占据剩余空间 */
  overflow: hidden; /* 防止子元素溢出撑开父容器 */
  position: relative;
}

.video-grid {
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 20px;
  margin-bottom: 24px;
  align-items: start; /* 顶部对齐，避免不同高度的视频卡片错位 */
}

/* 回到顶部按钮自定义样式 */
.back-top-custom {
  background-color: #fff;
  width: 100%;
  height: 100%;
  border-radius: 50%;
  box-shadow: 0 0 6px rgba(0,0,0,0.12);
  text-align: center;
  line-height: 40px;
  color: #1989fa;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
}

.video-card {
  /* 卡片本身透明，避免影响缩略图尺寸与位置 */
  background: transparent;
  /* 略大一些的圆角，让卡片更柔和但仍保持方正感 */
  border-radius: 6px;
  overflow: visible;
  box-shadow: none;
  transition: all 0.3s ease;
  cursor: pointer;
  /* 去掉每个视频展示格的内边距 */
  padding: 0;
  position: relative;
}

.video-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
  border-color: rgba(24, 117, 240, 0.2);
}

.video-card-disabled {
  cursor: not-allowed;
  opacity: 0.8;
}

.video-card-disabled:hover {
  transform: none;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.video-card-failed {
  cursor: pointer;
  opacity: 0.85;
}

.video-card-failed:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
}

/* 备注信息悬浮层 - 毛玻璃胶囊样式 */
.video-remark-overlay {
  position: absolute;
  bottom: 16px;
  left: 50%;
  transform: translateX(-50%);
  padding: 6px 16px;
  font-size: 14px;
  color: #fff;
  line-height: 1.5;
  background: rgba(0, 0, 0, 0.25);
  backdrop-filter: blur(6px);
  -webkit-backdrop-filter: blur(6px);
  border-radius: 20px;
  max-width: 80%;
  min-width: 120px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  text-align: center;
  z-index: 9;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
  cursor: default;
  transition: all 0.2s ease;
}

.video-remark-overlay.is-clickable {
  cursor: pointer;
}

.video-remark-overlay.is-clickable:hover {
  background: rgba(0, 0, 0, 0.35);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

/* 排队/处理中状态：备注在顶部显示 */
.video-remark-overlay.has-progress,
.video-remark-overlay.has-animation {
  top: 10px;
  bottom: auto;
  transform: translateX(-50%);
  z-index: 11;
}

.video-thumbnail {
  position: relative;
  width: 100%;
  /* 缩略图容器使用黑色背景并做圆角裁剪，圆角不改变内部视频尺寸 */
  background: #000;
  border-radius: 6px;
  overflow: hidden;
  padding-top: 56.25%; /* 16:9 比例，统一所有视频卡片的高度 */
  z-index: 2;
}

/* 竖版视频在容器内居中显示，上下留白 */
.video-thumbnail.video-portrait .video-placeholder,
.video-thumbnail.video-portrait .thumbnail-video,
.video-thumbnail.video-portrait .thumbnail-image {
  width: auto;
  height: 100%;
  max-width: 100%;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
}

/* 正方形视频同样居中显示 */
.video-thumbnail.video-square .video-placeholder,
.video-thumbnail.video-square .thumbnail-video,
.video-thumbnail.video-square .thumbnail-image {
  width: auto;
  height: 100%;
  max-width: 100%;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
}

.thumbnail-image,
.thumbnail-video {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  /* 使用 cover 让缩略图铺满容器，避免上下/四周出现黑色留白 */
  object-fit: cover;
  object-position: center;
}

/* 横版视频留白 */
.video-thumbnail.video-landscape .video-placeholder,
.video-thumbnail.video-landscape .thumbnail-video,
.video-thumbnail.video-landscape .thumbnail-image {
  /* 去掉横版视频四周留白，让内容铺满容器 */
  width: 100%;
  height: 100%;
  max-width: 100%;
  max-height: 100%;
}

/* 竖版和正方形视频使用 contain 确保完整显示 */
.video-thumbnail.video-portrait .video-placeholder,
.video-thumbnail.video-portrait .thumbnail-video,
.video-thumbnail.video-portrait .thumbnail-image,
.video-thumbnail.video-square .video-placeholder,
.video-thumbnail.video-square .thumbnail-video,
.video-thumbnail.video-square .thumbnail-image {
  width: auto;
  height: 100%;
  max-width: 100%;
  max-height: 100%;
}

/* 竖屏视频采用左右留黑的方式，保证完整高度展示 */
.video-thumbnail.video-portrait .thumbnail-video,
.video-thumbnail.video-portrait .thumbnail-image {
  object-fit: contain;
}

/* 竖版和正方形视频使用黑色背景 */
.video-portrait .thumbnail-image,
.video-portrait .thumbnail-video,
.video-square .thumbnail-image,
.video-square .thumbnail-video {
  background: #000;
}

/* 视频占位符 */
.video-placeholder {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  /* 占位也使用纯黑背景，避免与外层卡片之间形成灰色留白 */
  background: #000;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  border: none;
}

.video-placeholder-processing {
  background: transparent;
}

/* 横版视频占位符留白 */
.video-thumbnail.video-landscape .video-placeholder {
  /* 占位铺满容器，去掉四周留白 */
  width: 100%;
  height: 100%;
  max-width: 100%;
  max-height: 100%;
}

/* 竖版和正方形视频占位符 */
.video-thumbnail.video-portrait .video-placeholder,
.video-thumbnail.video-square .video-placeholder {
  width: 100%;
  height: 100%;
  max-width: 100%;
  max-height: 100%;
}

.placeholder-content {
  text-align: center;
  padding: 20px;
  width: 100%;
  position: relative;
  z-index: 3;
}

.placeholder-icon {
  font-size: 48px;
  color: #c0c4cc;
  margin-bottom: 12px;
  display: flex;
  justify-content: center;
}

.placeholder-status {
  font-size: 12px;
  color: #909399;
  margin-bottom: 8px;
}

/* 处理中状态的动态效果 - 椭圆点动画 */
.placeholder-status-queued {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
}

.placeholder-status-processing {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
}

.queued-text {
  letter-spacing: 1px;
}

.lds-ellipsis,
.lds-ellipsis div {
  box-sizing: border-box;
}

.lds-ellipsis {
  display: inline-block;
  position: relative;
  width: 48px;
  height: 48px;
  color: #409EFF; /* 处理中状态颜色 */
}

.lds-ellipsis div {
  position: absolute;
  top: 20px;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: currentColor;
  animation-timing-function: cubic-bezier(0, 1, 1, 0);
}

.lds-ellipsis div:nth-child(1) {
  left: 4.8px;
  animation: lds-ellipsis1 0.6s infinite;
}

.lds-ellipsis div:nth-child(2) {
  left: 4.8px;
  animation: lds-ellipsis2 0.6s infinite;
}

.lds-ellipsis div:nth-child(3) {
  left: 19.2px;
  animation: lds-ellipsis2 0.6s infinite;
}

.lds-ellipsis div:nth-child(4) {
  left: 33.6px;
  animation: lds-ellipsis3 0.6s infinite;
}

@keyframes lds-ellipsis1 {
  0% {
    transform: scale(0);
  }
  100% {
    transform: scale(1);
  }
}

@keyframes lds-ellipsis3 {
  0% {
    transform: scale(1);
  }
  100% {
    transform: scale(0);
  }
}

@keyframes lds-ellipsis2 {
  0% {
    transform: translate(0, 0);
  }
  100% {
    transform: translate(14.4px, 0);
  }
}

/* Small, aligned running frames soften the source animation's pose changes. */
.processing-text {
  letter-spacing: 1px;
}

.video-running-loader {
  display: block;
  position: relative;
  top: 8px;
  width: 30px;
  height: 36px;
  flex-shrink: 0;
  pointer-events: none;
  user-select: none;
}

.video-running-loader img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.error-message-display {
  margin-top: 12px;
  padding: 8px 12px;
  color: #f56c6c;
  font-size: 11px;
  line-height: 1.5;
  max-width: 100%;
  word-break: break-word;
  text-align: center;
  max-height: 60px;
  overflow-y: auto;
  box-sizing: border-box;
}

/* 本地失败任务详情弹窗 */
.local-error-detail {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 4px 0;
}

.local-error-status-bar {
  display: flex;
  align-items: center;
  gap: 8px;
  padding-bottom: 14px;
  border-bottom: 1px solid #f0f0f0;
}

.local-error-status-icon {
  font-size: 18px;
  color: #e6a23c;
}

.local-error-status-text {
  font-size: 15px;
  font-weight: 600;
  color: #e6a23c;
}

.local-error-time {
  margin-left: auto;
  font-size: 12px;
  color: #c0c4cc;
}

.local-error-friendly {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 14px 18px;
  background: #fffbf0;
  border: 1px solid #faecd8;
  border-radius: 10px;
  color: #b8860b;
  font-size: 14px;
  line-height: 1.6;
}

.local-error-icon {
  font-size: 16px;
  color: #e6a23c;
  flex-shrink: 0;
}

.local-error-raw {
  padding: 10px 14px;
  background: #f9f9f9;
  border-radius: 6px;
  color: #a8abb2;
  font-size: 12px;
  line-height: 1.7;
  word-break: break-all;
}

.local-error-section {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.local-error-label {
  font-size: 12px;
  color: #909399;
  font-weight: 500;
}

.local-error-prompt {
  font-size: 14px;
  color: #303133;
  line-height: 1.8;
  word-break: break-word;
  padding: 12px 14px;
  background: #fafafa;
  border-radius: 6px;
  border: 1px solid #f0f0f0;
  max-height: 160px;
  overflow-y: auto;
}

.local-error-meta {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
  padding: 14px 16px;
  background: #fafafa;
  border-radius: 8px;
}

.local-error-meta-item {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.local-error-meta-key {
  font-size: 11px;
  color: #c0c4cc;
}

.local-error-meta-val {
  font-size: 13px;
  color: #606266;
}

/* 失败状态：友好提示样式 */
.failed-hint {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  padding: 12px 10px;
  box-sizing: border-box;
}

.failed-hint-icon {
  font-size: 20px;
  color: #e6a23c;
}

.failed-hint-text {
  font-size: 12px;
  color: #909399;
  line-height: 1.4;
}

.failed-hint-link {
  font-size: 11px;
  color: #409eff;
  cursor: pointer;
  text-decoration: underline;
}

/* 错误信息滚动条样式 */
.error-message-display::-webkit-scrollbar {
  width: 4px;
}

.error-message-display::-webkit-scrollbar-track {
  background: rgba(245, 108, 108, 0.1);
  border-radius: 2px;
}

.error-message-display::-webkit-scrollbar-thumb {
  background: rgba(245, 108, 108, 0.5);
  border-radius: 2px;
}

.error-message-display::-webkit-scrollbar-thumb:hover {
  background: rgba(245, 108, 108, 0.7);
}

.video-thumbnail .reference-thumbnail { width: 100% !important; height: 100%; }

.thumbnail-video {
  pointer-events: none; /* 防止点击视频播放，由鼠标悬停控制 */
}

.video-duration {
  position: absolute;
  bottom: 8px;
  right: 8px;
  padding: 4px 8px;
  background: rgba(0, 0, 0, 0.7);
  color: #fff;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
}

/* 平台标签样式 */
.platform-tag {
  position: absolute;
  top: 8px;
  left: 8px;
  padding: 4px 10px;
  backdrop-filter: blur(4px);
  border-radius: 6px;
  font-size: 11px;
  font-weight: 600;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
  max-width: calc(100% - 16px);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  z-index: 1;
  pointer-events: none;
  transition: all 0.2s ease;
}

.platform-tag:hover {
  transform: scale(1.05);
}

.play-overlay {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 60px;
  height: 60px;
  background: rgba(64, 158, 255, 0.9);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: all 0.3s ease;
}

.video-card:hover .play-overlay {
  opacity: 1;
}

.play-icon {
  font-size: 24px;
  color: #fff;
  margin-left: 4px;
}

.progress-overlay {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: rgba(0, 0, 0, 0.6);
  padding: 8px 12px;
  z-index: 10;
}

.progress-text {
  color: #fff;
  font-size: 12px;
  text-align: center;
  margin-top: 4px;
}

.status-badge {
  position: absolute;
  top: 8px;
  left: 12px;
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
  color: #fff;
  z-index: 10;
}

/* 复制按钮（所有状态都显示） */
.copy-icon-wrapper {
  position: absolute;
  top: 50%;
  right: 10px;
  transform: translateY(-50%);
  z-index: 12;
  opacity: 0;
  transition: opacity 0.2s ease;
}

/* 当有下载或重试按钮时，复制按钮显示在下方 */
.video-card:has(.download-icon-wrapper) .copy-icon-wrapper {
  top: 65%;
}

.video-card:hover .copy-icon-wrapper {
  opacity: 1;
}

.copy-icon-button {
  width: 32px;
  height: 32px;
  padding: 0;
  background: rgba(0, 0, 0, 0.6);
  border: none;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
}

.copy-icon-button:hover {
  background: rgba(0, 0, 0, 0.9);
}

.copy-icon-button :deep(.el-icon) {
  font-size: 18px;
  line-height: 1;
  width: 18px;
  height: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.copy-icon-button :deep(.el-icon svg) {
  display: block;
}

/* 列表卡片下载按钮 */
.download-icon-wrapper {
  position: absolute;
  top: 40%;
  right: 10px;
  transform: translateY(-50%);
  z-index: 12;
  opacity: 0;
  transition: opacity 0.2s ease;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.download-icon-wrapper :deep(.el-button + .el-button) {
  margin-left: 0 !important;
}

.video-card:hover .download-icon-wrapper {
  opacity: 1;
}

.download-icon-button {
  width: 32px;
  height: 32px;
  padding: 0;
  background: rgba(0, 0, 0, 0.6);
  border: none;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
}

.download-icon-button:hover {
  background: rgba(0, 0, 0, 0.9);
}

.download-icon-button :deep(.el-icon) {
  font-size: 18px;
  line-height: 1;
  width: 18px;
  height: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.download-icon-button :deep(.el-icon svg) {
  display: block;
}

/* 编辑备注按钮 */
.edit-remark-icon-wrapper {
  position: absolute;
  top: 50%;
  left: 10px;
  transform: translateY(-50%);
  z-index: 12;
  opacity: 0;
  transition: opacity 0.2s ease;
}

.video-card:hover .edit-remark-icon-wrapper {
  opacity: 1;
}

.edit-remark-icon-button {
  width: 32px;
  height: 32px;
  padding: 0;
  background: rgba(0, 0, 0, 0.6);
  border: none;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
}

.edit-remark-icon-button:hover {
  background: rgba(0, 0, 0, 0.9);
}

.edit-remark-icon-button :deep(.el-icon) {
  font-size: 18px;
}

.delete-button {
  position: absolute;
  top: 8px;
  right: 8px;
  width: 32px;
  height: 32px;
  padding: 0;
  background: rgba(0, 0, 0, 0.5);
  border: none;
  color: #fff;
  font-size: 18px;
  z-index: 10;
  transition: all 0.3s ease;
  backdrop-filter: blur(4px);
}

.delete-button:hover {
  background: rgba(245, 108, 108, 0.9);
  transform: scale(1.1);
}

.delete-button:active {
  transform: scale(0.95);
}

/* 刷新按钮（中心位置） */
.refresh-icon-wrapper {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  opacity: 0;
  transition: opacity 0.3s ease;
  z-index: 5;
}

.video-card:hover .refresh-icon-wrapper {
  opacity: 1;
}

.refresh-icon-button {
  width: 40px;
  height: 40px;
  padding: 0;
  background: rgba(0, 0, 0, 0.3);
  border: 1px solid rgba(255, 255, 255, 0.5);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
}

.refresh-icon-button:hover {
  background: rgba(0, 0, 0, 0.6);
  border-color: rgba(255, 255, 255, 0.8);
  transform: scale(1.1);
}

.refresh-icon-button :deep(.el-icon) {
  font-size: 20px;
  line-height: 1;
  width: 20px;
  height: 20px;
}

.refresh-icon-button :deep(.el-icon svg) {
  display: block;
}

.status-success {
  background: linear-gradient(135deg, #1875F0 0%, #00C6FF 100%);
  color: #fff;
  border: none;
  box-shadow: 0 2px 8px rgba(24, 117, 240, 0.3);
}

.status-warning {
  background: linear-gradient(135deg, #FFA726 0%, #FFB74D 100%);
  color: #fff;
  border: none;
}

.status-info {
  background: linear-gradient(135deg, #42A5F5 0%, #64B5F6 100%);
  color: #fff;
  border: none;
}

.status-danger {
  background: linear-gradient(135deg, #EF5350 0%, #E57373 100%);
  color: #fff;
  border: none;
}

.load-more-container {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 20px;
  margin-top: 20px;
}

/* 缓存设置对话框样式 */
/* 生成统计弹窗及图表样式优化 */
.weekly-stats-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
}

.weekly-stats-dialog {
  width: 960px;
  max-width: 95vw;
  max-height: 90vh;
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.16);
  padding: 20px 24px 24px;
  display: flex;
  flex-direction: column;
}

.weekly-stats-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 24px;
}

.weekly-stats-title {
  font-size: 18px;
  font-weight: 600;
  color: #1A2B45;
}

.weekly-stats-close {
  color: #909399;
}

.weekly-stats-body {
  flex: 1;
  overflow: hidden;
  min-height: 320px;
  position: relative;
}

.weekly-stats-chart {
  display: flex;
  flex-direction: row;
  align-items: flex-end;
  gap: 16px;
  position: relative; /* 为图例定位提供参照 */
  padding-top: 30px; /* 顶部留白防止柱子顶到头 */
}

/* 图例悬浮右上角 */
.stat-legend-overlay {
  position: absolute;
  top: 0;
  right: 16px;
  display: flex;
  gap: 16px;
  font-size: 12px;
  color: #606266;
  z-index: 10;
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 6px;
}

.legend-item .legend-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  display: inline-block;
}

.legend-item.success .legend-dot {
  background: #1890FF;
}

.legend-item.failed .legend-dot {
  background: #FF4D4F;
}

/* Y轴样式 */
.stat-axis-y {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  height: 280px; /* 与图表高度一致 */
  font-size: 12px;
  color: #909399;
  padding-right: 12px;
  /* 留出与右侧日期相同的高度，让 0 对齐 X 轴线 */
  padding-bottom: 24px;
  box-sizing: border-box;
}

.stat-axis-label {
  position: relative;
  text-align: right;
  /* 消除文字高度对排版的影响，并垂直居中对齐刻度线 */
  height: 0;
  display: flex;
  align-items: center;
  justify-content: flex-end;
}

/* 刻度短横线 */
.stat-axis-label::after {
  content: '';
  position: absolute;
  right: -12px;
  width: 6px;
  border-bottom: 1px solid #ebeef5;
}

/* 核心图表区域 */
.stat-chart-bars {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 12px;
  height: 280px; /* 固定高度 */
  flex: 1;
  position: relative; /* 关键：用于定位X轴线 */
  padding: 0 16px;
}

/* 网格线容器 */
.stat-grid {
  position: absolute;
  left: 16px;
  right: 16px;
  top: 0;
  bottom: 24px; /* 与 X 轴线 bottom 一致 */
  display: flex;
  flex-direction: column-reverse;
  justify-content: space-between;
  pointer-events: none;
  z-index: 1;
}

.stat-grid-line {
  width: 100%;
  border-top: 1px dashed #ebeef5;
}

/* X轴横线 */
.stat-axis-x-line {
  position: absolute;
  bottom: 24px; /* 根据日期高度预留空间 */
  left: 0;
  right: 0;
  height: 1px;
  background: #dcdfe6; /* 轴线颜色 */
  z-index: 1;
}

.stat-bar-item {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  /* 不再使用 gap，靠固定高度或边距控制 */
  position: relative;
  height: 100%; /* 撑满容器高度 */
  justify-content: flex-end; /* 内容底部对齐 */
}

/* 柱子顶部的数值 */
.stat-bar-total {
  font-size: 14px;
  font-weight: 600;
  color: #1F2D3D;
  margin-bottom: 4px; /* 距离柱子顶部的距离 */
  min-height: 20px; /* 占位防止跳动 */
}

.stat-bar-total.zero {
  opacity: 0; /* 0值不显示数字，保持占位 */
}

/* 柱子容器 */
.stat-bar-stack {
  width: 100%;
  max-width: 48px;
  background: linear-gradient(180deg, rgba(24, 117, 240, 0.1), rgba(24, 117, 240, 0.25));
  border-radius: 4px 4px 0 0; /* 仅顶部圆角 */
  display: flex;
  flex-direction: column-reverse;
  overflow: hidden;
  position: relative;
  z-index: 2; /* 位于轴线上方 */
}

.stat-bar-stack--empty {
  background: transparent; /* 空数据时不显示背景色 */
}

.stat-bar-placeholder {
  width: 100%;
  height: 1px;
}

.stat-segment {
  width: 100%;
  min-height: 4px;
}

.stat-segment.success {
  background: linear-gradient(180deg, #1890FF, #40A9FF);
}

.stat-segment.failed {
  background: linear-gradient(180deg, #FF7875, #FF4D4F);
}

/* X轴刻度线 (Tick) */
.stat-bar-tick {
  width: 1px;
  height: 6px;
  background: #dcdfe6;
  margin-bottom: -3px; /* 让刻度线的一半骑在轴线上 */
  z-index: 3;
}

/* 日期文本 */
.stat-bar-date {
  font-size: 12px;
  color: #606266;
  margin-top: 8px; /* 距离轴线的距离 */
  height: 16px; /* 固定高度确保对齐 */
  line-height: 16px;
}

:deep(.daily-stats-dialog) {
  border-radius: 14px;
  overflow: hidden;
  box-shadow: 0 18px 48px rgba(15, 23, 42, 0.16);
}

:deep(.daily-stats-dialog .el-dialog__header) {
  padding: 0;
  margin: 0;
}

:deep(.daily-stats-dialog .el-dialog__body) {
  padding: 0 24px 24px;
}

.daily-stats-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 22px 24px 18px;
  border-bottom: 1px solid #f0f2f5;
  background: linear-gradient(180deg, #fbfdff 0%, #ffffff 100%);
}

.daily-stats-title-wrap {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
}

.daily-stats-title-icon {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: #1875F0;
  background: linear-gradient(135deg, #e6f7ff 0%, #e0f2fe 100%);
  box-shadow: inset 0 0 0 1px rgba(24, 117, 240, 0.08);
}

.daily-stats-title-icon :deep(.el-icon) {
  font-size: 18px;
}

.daily-stats-title {
  color: #111827;
  font-size: 18px;
  line-height: 1.3;
  font-weight: 650;
}

.daily-stats-subtitle {
  margin-top: 3px;
  color: #909399;
  font-size: 12px;
  line-height: 1.4;
}

.daily-stats-close {
  width: 30px;
  height: 30px;
  border: none;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: #9ca3af;
  background: transparent;
  cursor: pointer;
  transition: all 0.18s ease;
}

.daily-stats-close:hover {
  color: #606266;
  background: #f3f4f6;
}

.daily-stats-content {
  min-height: 188px;
  padding-top: 18px;
}

.daily-stats-date {
  width: fit-content;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 5px 10px;
  color: #606266;
  font-size: 13px;
  line-height: 1.4;
  border: 1px solid #e4e7ed;
  border-radius: 999px;
  background: #f8fafc;
  margin-bottom: 14px;
}

.daily-stats-date :deep(.el-icon) {
  color: #13A8A8;
  font-size: 14px;
}

.daily-stats-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px;
}

.daily-stats-item {
  min-height: 128px;
  padding: 16px;
  border: 1px solid #e6e9ee;
  border-radius: 10px;
  background: #ffffff;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  box-shadow: 0 8px 22px rgba(15, 23, 42, 0.05);
  transition: border-color 0.18s ease, box-shadow 0.18s ease, transform 0.18s ease;
}

.daily-stats-item:hover {
  border-color: rgba(24, 117, 240, 0.32);
  box-shadow: 0 10px 26px rgba(24, 117, 240, 0.11);
  transform: translateY(-1px);
}

.daily-stats-item--count .daily-stats-card-icon {
  color: #1875F0;
  background: #ecf5ff;
}

.daily-stats-item--duration .daily-stats-card-icon {
  color: #13A8A8;
  background: #e6fffb;
}

.daily-stats-item-head {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
}

.daily-stats-card-icon {
  width: 28px;
  height: 28px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.daily-stats-card-icon :deep(.el-icon) {
  font-size: 16px;
}

.daily-stats-label {
  color: #606266;
  font-size: 13px;
  font-weight: 500;
  white-space: nowrap;
}

.daily-stats-metric {
  display: flex;
  align-items: baseline;
  gap: 6px;
  min-width: 0;
}

.daily-stats-value {
  color: #1a2b45;
  font-size: 38px;
  font-weight: 750;
  line-height: 1;
  letter-spacing: 0;
}

.daily-stats-value--duration {
  font-size: 26px;
  line-height: 1.22;
  word-break: break-word;
}

.daily-stats-unit {
  color: #909399;
  font-size: 13px;
  font-weight: 500;
}

.loading-more {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #409EFF;
  font-size: 14px;
}

.loading-more .el-icon {
  font-size: 16px;
  animation: rotating 2s linear infinite;
}

@keyframes rotating {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.load-more-tip {
  color: #909399;
  font-size: 14px;
}

/* 手机端适配 */
@media (max-width: 900px) {
  .video-list-container :deep(.sidebar) {
    z-index: 1002;
  }

  .video-list-container :deep(.video-header) {
    left: 0;
  }

  .navigation-collapsed .video-content {
    padding-top: 16px;
  }
  :deep(.daily-stats-dialog) {
    width: calc(100vw - 32px) !important;
    max-width: 500px;
  }

  :deep(.daily-stats-dialog .el-dialog__body) {
    padding: 0 16px 18px;
  }

  .daily-stats-header {
    padding: 18px 16px 14px;
  }

  .daily-stats-title-icon {
    width: 32px;
    height: 32px;
    border-radius: 8px;
  }

  .daily-stats-title {
    font-size: 16px;
  }

  .daily-stats-subtitle {
    max-width: 220px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .daily-stats-content {
    min-height: 0;
    padding-top: 14px;
  }

  .daily-stats-grid {
    grid-template-columns: 1fr;
    gap: 10px;
  }

  .daily-stats-item {
    min-height: 104px;
    padding: 14px;
  }

  .daily-stats-value {
    font-size: 34px;
  }

  .daily-stats-value--duration {
    font-size: 24px;
  }

  .content-area {
    margin-left: 0;
  }

  .video-content {
    /* 移动端减去顶部高度 */
    height: calc(100vh - 60px);
    padding: 16px;
    padding-top: 76px;
    padding-right: 16px;
    padding-bottom: 250px;
  }

  .generate-container {
    left: 16px;
    right: 16px;
    bottom: 16px;
    padding: 12px;
    max-width: calc(100% - 32px);
  }

  .generate-form {
    flex-direction: column;
    gap: 10px;
  }

  .prompt-input-container {
    width: 100%;
  }

  .seconds-select,
  .size-select,
  .platform-select {
    width: 100%;
  }

  .generate-button {
    width: 100%;
  }

  .delete-button {
    width: 24px;
    height: 24px;
    font-size: 14px;
    top: 6px;
    right: 6px;
  }

  .refresh-icon-button {
    width: 32px;
    height: 32px;
  }

  .refresh-icon-button :deep(.el-icon) {
    font-size: 16px;
    width: 16px;
    height: 16px;
  }

  .video-grid {
    grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
    gap: 12px;
    margin-bottom: 16px;
  }

  .video-remark-overlay {
    bottom: 12px;
    padding: 5px 12px;
    font-size: 12px;
    max-width: 75%;
    min-width: 100px;
    border-radius: 18px;
    background: rgba(0, 0, 0, 0.25);
    backdrop-filter: blur(6px);
    -webkit-backdrop-filter: blur(6px);
    text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    text-align: center;
  }

  .video-remark-overlay.has-progress {
    bottom: 52px;
  }

  .video-remark-overlay.has-animation {
    bottom: 56px;
  }

  .error-message {
    font-size: 11px;
    padding: 6px;
  }

  .load-more-container {
    padding: 16px 8px;
  }
}

/* 小屏手机适配 */
@media (prefers-reduced-motion: reduce) {
  .video-list-container {
    --navigation-duration: 0ms;
  }
}

@media (max-width: 480px) {
  .video-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 8px;
  }

  .video-card {
    border-radius: 0;
  }

  .upload-icon-button {
    width: 50px;
    height: 50px;
  }

  .image-preview-wrapper {
    width: 50px;
    height: 50px;
  }

  .remove-image-button {
    width: 18px;
    height: 18px;
    font-size: 10px;
  }

  .placeholder-icon {
    font-size: 32px;
    margin-bottom: 6px;
  }

  .placeholder-status {
    font-size: 10px;
    margin-bottom: 6px;
  }

  .error-message-display {
    margin-top: 10px;
    padding: 10px;
    font-size: 11px;
    line-height: 1.6;
    max-height: 80px;
    text-align: center;
  }

  .placeholder-content {
    padding: 12px;
  }

  .video-info {
    padding: 10px;
  }

  .video-title {
    font-size: 13px;
    margin-bottom: 6px;
  }

  .video-meta {
    font-size: 11px;
  }

  .video-stats {
    font-size: 11px;
    gap: 8px;
  }

  .play-overlay {
    width: 40px;
    height: 40px;
  }

  .play-icon {
    font-size: 18px;
  }
}

/* 删除确认对话框全局样式 */
:global(.delete-confirm-dialog) {
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15);
}

:global(.delete-confirm-dialog .el-message-box__header) {
  padding: 24px 24px 16px;
  text-align: center;
  border-bottom: none;
}

:global(.delete-confirm-dialog .el-message-box__title) {
  font-size: 20px;
  font-weight: 700;
  color: #1A2B45;
  margin: 0;
}

:global(.delete-confirm-dialog .el-message-box__message) {
  padding: 0;
  margin: 0;
}

:global(.delete-dialog-content) {
  text-align: center;
  padding: 8px 0;
}

:global(.delete-icon-wrapper) {
  width: 80px;
  height: 80px;
  margin: 0 auto 20px;
  background: rgba(255, 77, 79, 0.1);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

:global(.delete-icon-large) {
  width: 48px;
  height: 48px;
  color: #FF4D4F;
}

:global(.delete-message) {
  color: #606266;
  font-size: 15px;
  line-height: 1.8;
  margin: 0;
}

:global(.delete-confirm-dialog .el-message-box__btns) {
  padding: 20px 24px 24px;
  text-align: center;
}

:global(.delete-cancel-button) {
  background: #f5f7fa;
  border: 1px solid #dcdfe6;
  color: #606266;
  border-radius: 24px;
  padding: 10px 24px;
  font-size: 14px;
  transition: all 0.3s ease;
}

:global(.delete-cancel-button:hover) {
  background: #ecf5ff;
  border-color: #b3d8ff;
  color: #409eff;
}

:global(.delete-confirm-button) {
  background: linear-gradient(135deg, #FF7875 0%, #F5222D 100%);
  border: none;
  color: #fff;
  border-radius: 24px;
  padding: 10px 24px;
  font-size: 14px;
  font-weight: 600;
  box-shadow: 0 4px 12px rgba(245, 34, 45, 0.3);
  transition: all 0.3s ease;
  margin-left: 12px;
}

:global(.delete-confirm-button:hover) {
  background: linear-gradient(135deg, #F5222D 0%, #FF7875 100%);
  box-shadow: 0 6px 20px rgba(245, 34, 45, 0.4);
  transform: translateY(-2px);
}

:global(.delete-confirm-button:active) {
  transform: translateY(0);
}

/* 对话框遮罩背景模糊 */
:global(.el-overlay) {
  backdrop-filter: blur(5px);
}

:global(.el-message-box) {
  border-radius: 20px;
}

/* 退出登录确认对话框样式 */
:global(.logout-confirm-dialog) {
  border-radius: 20px !important;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15) !important;
  background: #fff !important;
  overflow: hidden;
}

:global(.logout-confirm-dialog .el-message-box__header) {
  padding: 20px 20px 10px !important;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  border-bottom: none !important;
}

:global(.logout-confirm-dialog .el-message-box__headerbtn) {
  top: 15px !important;
  right: 15px !important;
  font-size: 18px !important;
  color: #909399 !important;
  transition: all 0.3s ease;
}

:global(.logout-confirm-dialog .el-message-box__headerbtn:hover) {
  color: #f56c6c !important;
  transform: scale(1.1);
}

:global(.logout-confirm-dialog .el-message-box__title) {
  font-size: 20px !important;
  font-weight: 700 !important;
  color: #1A2B45 !important;
  margin-top: 10px;
}

:global(.logout-confirm-dialog .el-message-box__content) {
  padding: 10px 20px 20px !important;
  font-size: 15px !important;
  color: #606266 !important;
  line-height: 1.8 !important;
  text-align: center !important;
}

:global(.logout-confirm-dialog .el-message-box__btns) {
  padding: 0 20px 20px !important;
  display: flex;
  justify-content: center;
  gap: 15px;
}

:global(.logout-confirm-dialog .el-button) {
  border-radius: 24px !important;
  min-width: 100px;
  height: 40px;
  font-weight: 500 !important;
  transition: all 0.3s ease;
}

:global(.logout-confirm-dialog .el-button--default) {
  background: #f0f2f5 !important;
  border-color: #f0f2f5 !important;
  color: #606266 !important;
}

:global(.logout-confirm-dialog .el-button--default:hover) {
  background: #e4e7ed !important;
  border-color: #e4e7ed !important;
  color: #303133 !important;
}

:global(.logout-confirm-dialog .el-button--primary) {
  background: linear-gradient(90deg, #1875F0 0%, #00C6FF 100%) !important;
  border: none !important;
  color: #fff !important;
  font-weight: 600 !important;
  font-size: 16px !important;
  box-shadow: 0 4px 12px rgba(24, 117, 240, 0.3) !important;
}

:global(.logout-confirm-dialog .el-button--primary:hover) {
  background: linear-gradient(90deg, #00C6FF 0%, #1875F0 100%) !important;
  box-shadow: 0 6px 16px rgba(24, 117, 240, 0.4) !important;
  transform: translateY(-2px);
}

:global(.logout-confirm-dialog .el-button--primary:active) {
  transform: translateY(0);
}

.presets-list {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
  max-height: calc(140px * 4 + 12px * 3); /* 四行高度 + 三个间距 */
  overflow-y: auto;
  padding: 4px;
  scrollbar-width: thin;
  scrollbar-color: #c1c1c1 #f1f1f1;
}

.presets-list::-webkit-scrollbar {
  width: 6px;
}

.presets-list::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}

.presets-list::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 3px;
}

.presets-list::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}

.preset-empty {
  grid-column: 1 / -1;
  text-align: center;
  padding: 40px 20px;
  color: #909399;
  font-size: 14px;
}

.preset-card {
  border: 2px solid #e4e7ed;
  border-radius: 12px;
  padding: 16px;
  background: #fff;
  position: relative;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  display: flex;
  flex-direction: column;
  gap: 12px;
  min-height: 140px;
  cursor: pointer;
}

.preset-card:hover {
  border-color: #409EFF;
  background-color: #f0f9ff;
  transform: translateY(-2px);
  box-shadow: 0 4px 16px rgba(64, 158, 255, 0.15);
}

.preset-card-selected {
  border-color: #409EFF;
  background: linear-gradient(135deg, #f0f9ff 0%, #ffffff 100%);
  box-shadow: 0 4px 20px rgba(64, 158, 255, 0.2);
}

.preset-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
}

.preset-title-wrapper {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  min-width: 0;
}

.preset-type-tag {
  flex-shrink: 0;
  font-size: 11px;
  padding: 2px 6px;
  height: 20px;
  line-height: 16px;
}

.preset-title {
  font-size: 15px;
  font-weight: 600;
  color: #303133;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex: 1;
}

.preset-desc {
  font-size: 13px;
  color: #606266;
  line-height: 1.6;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  flex: 1;
  min-height: 40px;
}

.preset-actions {
  display: flex;
  gap: 8px;
  justify-content: flex-end;
  opacity: 0;
  transition: opacity 0.2s ease;
  pointer-events: auto;
  margin-top: auto;
  padding-top: 8px;
  border-top: 1px solid #f0f0f0;
}

.preset-card:hover .preset-actions,
.preset-card-selected .preset-actions {
  opacity: 1;
}

.new-preset-btn-area {
  padding-bottom: 12px;
  border-bottom: 1px solid #eee;
  margin-bottom: 12px;
}
</style>

<style scoped src="./video-gallery.css"></style>

<style scoped>
.submission-actions { position: absolute; bottom: 30px; left: 8px; right: 8px; display: flex; flex-wrap: wrap; gap: 6px; justify-content: center; z-index: 3; }
.submission-actions span { width: 100%; font-size: 12px; color: #b45309; overflow-wrap: anywhere; }
</style>
